import * as THREE from 'three';
import { Batcher, boxGeo, cylGeo, sheetGeo, trs, vary, tint, shade, accent } from './Geometry.js';
import * as P from './Props.js';

/** ISO container height — every mantle chain on this map is measured off it. */
const CH = P.TUNING.containerH;

/**
 * OPERATION BLACKOUT — "DUST YARD"
 *
 * A sun-baked industrial back-lot at golden hour: roughly 104 x 104 m of playable
 * ground bounded by compound walls, warehouses and tenement blocks.
 *
 * ── Level design intent ───────────────────────────────────────────────────────
 *
 * Three north–south lanes, contested from the south spawn:
 *
 *   WEST   loading dock → warehouse interior (mezzanine + roof) → container yard
 *   CENTRE market street → plaza with the monument → jog east around the tenement
 *   EAST   shop row → two-storey residential block → workshop alley
 *
 * Two full-width cross streets (z ≈ +27 and z ≈ −35), the plaza, the warehouse's
 * east door and two through-buildings stitch the lanes together, so every route has
 * at least two exits and nothing is a cul-de-sac.
 *
 * Cover is authored at three heights — 0.86 m jersey barriers and sandbags to crouch
 * behind, 1.1–1.7 m HESCO, vehicles and containers to stand behind, and full walls to
 * break line of sight completely. Long lanes are chopped by a bus, trucks, container
 * stacks and jogs in the building line so no sightline runs past ~40 m.
 *
 * Verticality: warehouse mezzanine (3.3) and roof (6.4), a container stack (5.0)
 * that mantles onto that roof, the tenement balcony (3.5) and roof (7.0), the
 * workshop roof (5.9), the alley-block roof (4.9), the shop-row roof (4.7), the north
 * colonnade terrace (3.5) and a scaffold deck (4.0). Every one is reachable by
 * stairs, a rubble ramp, or a mantle chain whose individual rises stay inside the
 * player's 0.5–1.55 m mantle window.
 *
 * ── Technical notes ───────────────────────────────────────────────────────────
 *
 * Collision is AABB-only, so structure is authored from axis-aligned boxes and every
 * angled or round visual carries a hand-placed box approximation instead of a
 * bounding volume that would lie about where the wall is. Everything is merged by
 * surface into a couple of dozen draw calls; per-piece variation rides on a vertex
 * colour attribute, so merging costs nothing in visual repetition.
 */

export const TUNING = {
  half: 52,            // playable half-extent in metres
  wallHeight: 4.3,     // compound wall
  warehouseRoof: 6.4,
  warehouseMezz: 3.3,
  tenementFloor: 3.5,
  tenementRoof: 7.0,
  workshopRoof: 5.6,
  alleyBlockRoof: 4.6,
  shopRoof: 4.4,
  marketBlockRoof: 4.6,
  streetY: 0.02,       // asphalt sits 2 cm proud of the dirt
  floorY: 0.05,        // interior concrete slabs

  /**
   * Ground overlay stack.
   *
   * Everything that dresses the ground — the soft dirt patches, road patching,
   * potholes, tyre tracks, spillage — is flat geometry lying on a surface that is
   * already there, and a flat thing lying on another flat thing is a depth-buffer
   * problem before it is an art problem. Two coplanar top faces do not blend: the
   * depth test picks one per pixel, and which one it picks changes with the camera,
   * so the pair *swaps* as the player moves.
   *
   * That is what the ground was doing. `overlayLift` put the dirt patches' top face
   * at exactly `streetY`, the asphalt's own top face, so a third of 320 patches were
   * coplanar with every road on the map — and pale dirt fighting near-black asphalt
   * is a maximum-contrast swap. Standing still in the market street, mean ground
   * luminance alternated 37 / 85 between consecutive frames. It reads as a wet
   * shimmer travelling over the surface, which is exactly what "the ground looks like
   * it's reflecting" describes.
   *
   * The separations are small on purpose — these are 1.5 cm skins, not kerbs — and
   * they only have to beat the depth buffer, which at a grazing angle is easy:
   * two horizontal planes `dy` apart are `dy * d / eye` apart *along the view ray*,
   * so distance works in our favour. Against a 24-bit buffer at near 0.05 / far 600
   * the requirement comes out as
   *
   *     dy  >  d * eye * 1.19e-6
   *
   * and the worst case this map can produce is the longest sightline (55.6 m) seen
   * from the highest reachable eye (tenement roof, 7.0 + 1.65 m), which asks for
   * 0.57 mm. `overlayRung` is that number with margin; `overlayStep` is the coarser
   * gap between whole *families* of overlay, which also has to read as a physical
   * layer and not just win an argument with the z-buffer.
   */
  overlayRung: 0.0008,
  overlayStep: 0.002,
  /** Where the dirt-patch skin sits: clear of the dirt plane, clear of the asphalt. */
  overlayLift: 0.002,
  coverStandoff: 0.78, // how far a cover point sits off the face it hugs
  maxCoverPoints: 96,
};

/**
 * Every elevated surface that is actually connected to the walkable world, as an
 * explicit footprint rather than a height test — two roofs at the same height are not
 * interchangeable, and cover authored onto an island is cover nothing can use. Filler
 * blocks, the north block's main roof and the depot roof are deliberately absent:
 * they have no stair, ramp or mantle chain, so nothing is placed on them.
 */
const REACHABLE_PLATFORMS = [
  { y: 1.10, x0: -50.0, x1: -34.0, z0: 24.8, z1: 28.0 },   // depot loading dock
  { y: 3.30, x0: -45.0, x1: -38.5, z0: 6.0, z1: 19.7 },    // warehouse mezzanine
  { y: 6.40, x0: -45.3, x1: -25.7, z0: -4.3, z1: 20.5 },   // warehouse roof
  { y: 6.40, x0: -26.4, x1: -21.6, z0: 14.4, z1: 16.4 },   // warehouse gantry
  { y: CH, x0: -25.7, x1: -23.1, z0: -9.3, z1: 3.2 },    // warehouse container stack, lower
  { y: CH * 2, x0: -25.7, x1: -23.1, z0: -5.3, z1: 1.0 },    // warehouse container stack, upper
  { y: 3.70, x0: -25.2, x1: -23.6, z0: 1.0, z1: 2.4 },     // its intermediate crate
  { y: CH, x0: -34.1, x1: -27.9, z0: -15.8, z1: -10.3 }, // yard climb, lower
  { y: CH * 2, x0: -34.1, x1: -27.9, z0: -12.9, z1: -10.3 }, // yard climb, upper
  { y: 3.50, x0: 21.3, x1: 40.1, z0: 8.0, z1: 23.0 },      // tenement first floor + balcony
  { y: 7.00, x0: 24.0, x1: 40.0, z0: 8.0, z1: 23.0 },      // tenement roof
  { y: 5.90, x0: 28.6, x1: 44.4, z0: -26.4, z1: -7.6 },    // workshop roof
  { y: 4.90, x0: 15.6, x1: 25.9, z0: -30.4, z1: -15.6 },   // alley block roof
  { y: 5.90, x0: -24.4, x1: -3.6, z0: -30.4, z1: -13.6 },  // C1 roof
  { y: 4.70, x0: 9.6, x1: 32.4, z0: 32.6, z1: 45.4 },      // shop-row roof
  { y: 4.90, x0: 10.1, x1: 15.9, z0: 5.6, z1: 24.4 },      // east market block roof
  { y: 3.50, x0: -22.4, x1: 12.4, z0: -42.0, z1: -39.6 },  // north colonnade terrace
  { y: 4.06, x0: -14.1, x1: -10.9, z0: 0.2, z1: 2.8 },     // plaza scaffold deck
  { y: CH, x0: 15.6, x1: 18.2, z0: 17.5, z1: 23.7 },     // east block climb container
  { y: CH, x0: 9.9, x1: 16.1, z0: 30.0, z1: 32.5 },      // shop-row climb container
  { y: CH, x0: 17.9, x1: 24.1, z0: -15.7, z1: -13.1 },   // alley block climb container
  { y: 5.75, x0: 25.1, x1: 28.7, z0: -19.7, z1: -18.3 },   // workshop <-> alley block gangway
  { y: 5.40, x0: 36.1, x1: 39.1, z0: 5.2, z1: 7.6 },       // tenement roof descent scaffold
];

function onReachablePlatform(x, z, y) {
  for (const p of REACHABLE_PLATFORMS) {
    if (Math.abs(y - p.y) > 0.35) continue;
    if (x > p.x0 - 1 && x < p.x1 + 1 && z > p.z0 - 1 && z < p.z1 + 1) return true;
  }
  return false;
}

const H = TUNING.half;

export class Level {
  constructor(ctx) {
    this.ctx = ctx;
    this.root = new THREE.Group();
    this.root.name = 'level';

    this.playerSpawn = new THREE.Vector3(0, 0.25, 47);
    this.enemySpawns = [];
    this.coverPoints = [];
    this.navBounds = {
      min: new THREE.Vector3(-H, -1, -H),
      max: new THREE.Vector3(H, 16, H),
    };
  }

  build() {
    const { ctx } = this;
    this.rng = ctx.rng;
    this.b = new Batcher(ctx);

    // Instanced scatter sets carry the tertiary layer without touching draw calls.
    /** Every building footprint, collected as it is built and dressed at the end. */
    this.facades = [];
    this.stones = this.b.scatter('concrete', new THREE.IcosahedronGeometry(0.24, 0), 'receive');
    this.gravel = this.b.scatter('dirt', new THREE.IcosahedronGeometry(0.11, 0), 'none');

    this._ground();
    this._perimeter();
    this._warehouse();
    this._westYard();
    this._marketBlocks();
    this._centralStreet();
    this._plaza();
    this._tenement();
    this._workshop();
    this._shopRow();
    this._northBlock();
    this._fillerBlocks();
    this._breakers();
    this._overhead();
    this._facadeDetail();
    this._dressing();
    this._skyline();

    this.b.build(this.root);
    ctx.scene.add(this.root);
    // Merged batches are tagged noCollide; their colliders were registered per box on
    // the way in. This call is kept so the level still honours the module contract.
    ctx.physics.addStatic(this.root);

    this._makeCoverPoints();
    this._makeEnemySpawns();

    this.stats = {
      ...this.b.stats,
      batches: this.root.children.length,
      coverPoints: this.coverPoints.length,
      enemySpawns: this.enemySpawns.length,
    };
    console.info(
      `[level] ${this.b.stats.pieces} pieces, ${this.b.stats.colliders} colliders, ` +
      `${Math.round(this.b.stats.tris / 1000)}k tris, ${this.root.children.length} draw batches, ` +
      `${this.coverPoints.length} cover points, ${this.enemySpawns.length} enemy spawns`
    );
  }

  /* ------------------------------------------------------------------ */
  /*  authoring helpers                                                  */
  /* ------------------------------------------------------------------ */

  /** Axis-aligned box authored in min/max form — how level geometry actually thinks. */
  _box(surface, x0, x1, y0, y1, z0, z1, o = {}) {
    // Normalise: an inverted pair would build an inside-out mesh and an inverted
    // AABB that silently collides with nothing at all.
    if (x1 < x0) { const t = x0; x0 = x1; x1 = t; }
    if (y1 < y0) { const t = y0; y0 = y1; y1 = t; }
    if (z1 < z0) { const t = z0; z0 = z1; z1 = t; }
    this.b.solid(surface, (x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2, x1 - x0, y1 - y0, z1 - z0, o);
  }

  /** Ground slab: top at `top`, 0.34 m of body below so bullets never punch through. */
  _slab(surface, x0, x1, z0, z1, top, o = {}) {
    this._box(surface, x0, x1, top - 0.34, top, z0, z1, {
      tile: o.tile,
      color: o.color,
      shadow: 'receive',
      matId: o.matId,
      uOff: o.uOff,
      vOff: o.vOff,
    });
  }

  /**
   * Parapet ring around a roof. `skip` drops an edge — used wherever a mantle chain
   * arrives, because an unbroken parapet pushes the ledge above the player's reach.
   */
  _parapet(x0, x1, z0, z1, top, h = 0.92, o = {}) {
    const t = o.thick ?? 0.28;
    const col = o.color ?? tint(0x9a9285);
    const skip = o.skip ?? [];
    const opt = { tile: 1.6, color: col, cover: 'low' };
    if (!skip.includes('n')) this._box('concrete', x0, x1, top, top + h, z0, z0 + t, opt);
    if (!skip.includes('s')) this._box('concrete', x0, x1, top, top + h, z1 - t, z1, opt);
    if (!skip.includes('w')) this._box('concrete', x0, x0 + t, top, top + h, z0 + t, z1 - t, opt);
    if (!skip.includes('e')) this._box('concrete', x1 - t, x1, top, top + h, z0 + t, z1 - t, opt);
  }

  /** Solid massing block with a roof and parapet — corner filler and sight blocker. */
  _block(x0, x1, z0, z1, h, surface, hex) {
    const col = vary(this.rng, hex, 0.05);
    this.facades.push({ x0, x1, z0, z1, h, surface });
    this._box(surface, x0, x1, 0, h, z0, z1, { color: col });
    this._box('concrete', x0 - 0.4, x1 + 0.4, h, h + 0.35, z0 - 0.4, z1 + 0.4, { color: tint(0x8f8778) });
    this._parapet(x0 - 0.4, x1 + 0.4, z0 - 0.4, z1 + 0.4, h + 0.35, 0.8, { color: tint(0x9a9285) });
    // Even the roofs nothing can reach are read from the roofs that can be, so they
    // get the covering and the outlet too. Everything here is 3 cm of flat geometry.
    P.roofDeck(this.b, this.rng, x0 - 0.25, x1 + 0.25, z0 - 0.25, z1 + 0.25, h + 0.35, {
      outletX: x0 + (x1 - x0) * 0.68, outletZ: z0 + (z1 - z0) * 0.78, upstand: 0.22, seam: 1.15,
    });
    return col;
  }

  /**
   * A practical light source. Bloom only picks up values above ~1.7 after tone
   * mapping, so these carry a real HDR emissive rather than a pale grey panel —
   * otherwise a "lit" sign just reads as a slightly brighter rectangle.
   */
  _glow(x, y, z, w, h, d, rotY = 0, hex = 0xffc27a, intensity = 3.2) {
    this.b.add(
      'glass', boxGeo(w, h, d, 0.5), trs(x, y, z, 0, rotY, 0), tint(hex, { lum: 1.0 }), 'none', true,
      { emissive: hex, emissiveIntensity: intensity, toneMapped: true, roughness: 0.9, metalness: 0 }
    );
  }

  /**
   * Kerb run, with the gutter channel in front of it and the footway behind.
   *
   * A kerb on its own is a plank lying on the road: the reason a real kerb reads is
   * that it is the *edge of a raised pavement*, and there is a dished channel on the
   * road side collecting everything that runs off it. Both of those are what tell the
   * eye where the carriageway stops, and neither was here.
   *
   * `side` is +1 if the footway is on the +X/+Z side of the kerb line.
   */
  _kerb(x0, x1, z0, z1, o = {}) {
    const rng = this.rng;
    const alongX = x1 - x0 > z1 - z0;
    const len = alongX ? x1 - x0 : z1 - z0;
    const n = Math.max(1, Math.round(len / 2.2));
    const side = o.side ?? 0;
    const foot = o.foot ?? 0;
    const top = 0.17;
    for (let i = 0; i < n; i++) {
      const t0 = (i * len) / n;
      const t1 = ((i + 1) * len) / n - 0.05;
      const col = vary(rng, 0xa8a091, 0.07);
      // Dropped kerbs and a broken stone or two, so the line is not machined.
      const dropped = rng() < 0.09;
      const h = dropped ? 0.05 : top - rng() * 0.02;
      if (alongX) this._box('concrete', x0 + t0, x0 + t1, -0.2, h, z0, z1, { color: col, shadow: 'receive' });
      else this._box('concrete', x0, x1, -0.2, h, z0 + t0, z0 + t1, { color: col, shadow: 'receive' });
    }
    // Gutter channel: one setts-width strip on the carriageway side, sunk 3 cm below
    // the road so it catches a shadow along its whole length.
    const g = 0.34;
    if (side !== 0) {
      if (alongX) {
        const gz = side > 0 ? z0 - g : z1 + g;
        this._box('concrete', x0, x1, -0.2, TUNING.streetY - 0.03, Math.min(gz, side > 0 ? z0 : z1), Math.max(gz, side > 0 ? z0 : z1),
          { color: vary(rng, 0x9a9285, 0.06), shadow: 'receive', collide: false });
      } else {
        const gx = side > 0 ? x0 - g : x1 + g;
        this._box('concrete', Math.min(gx, side > 0 ? x0 : x1), Math.max(gx, side > 0 ? x0 : x1), -0.2, TUNING.streetY - 0.03, z0, z1,
          { color: vary(rng, 0x9a9285, 0.06), shadow: 'receive', collide: false });
      }
      // Gully gratings set into the channel every 12 m or so.
      const gn = Math.max(1, Math.round(len / 12));
      for (let i = 0; i < gn; i++) {
        const t = (len * (i + 0.5)) / gn;
        const px = alongX ? x0 + t : (side > 0 ? x0 - g / 2 : x1 + g / 2);
        const pz = alongX ? (side > 0 ? z0 - g / 2 : z1 + g / 2) : z0 + t;
        this._box('metalPainted', px - 0.19, px + 0.19, -0.05, TUNING.streetY - 0.015, pz - 0.15, pz + 0.15,
          { tile: 0.25, color: shade(0x4a453e, 0.42), collide: false, shadow: 'none' });
        for (let k = 0; k < 4; k++) {
          const o2 = -0.12 + k * 0.08;
          this._box('metalPainted', alongX ? px - 0.17 : px + o2 - 0.015, alongX ? px + 0.17 : px + o2 + 0.015,
            TUNING.streetY - 0.012, TUNING.streetY, alongX ? pz + o2 - 0.015 : pz - 0.13, alongX ? pz + o2 + 0.015 : pz + 0.13,
            { tile: 0.15, color: shade(0x6a655c, 0.55), collide: false, shadow: 'none' });
        }
      }
    }
    // Footway behind the kerb — a raised slab, not a painted line.
    if (foot > 0 && side !== 0) {
      const col = vary(rng, 0xa39b8c, 0.05);
      if (alongX) {
        const fz0 = side > 0 ? z1 : z0 - foot;
        this._slab('concrete', x0, x1, fz0, fz0 + foot, 0.15, { tile: 1.6, color: col });
      } else {
        const fx0 = side > 0 ? x1 : x0 - foot;
        this._slab('concrete', fx0, fx0 + foot, z0, z1, 0.15, { tile: 1.6, color: col });
      }
      // Lifted and cracked flags, and weeds in the joints.
      const fn = Math.max(2, Math.round(len / 3.0));
      for (let i = 0; i < fn; i++) {
        const t = (len * (i + 0.5)) / fn + (rng() - 0.5) * 1.2;
        const px = alongX ? x0 + t : (side > 0 ? x1 + foot * rng() : x0 - foot * rng());
        const pz = alongX ? (side > 0 ? z1 + foot * rng() : z0 - foot * rng()) : z0 + t;
        if (rng() < 0.4) {
          const w = 0.5 + rng() * 0.5;
          this.b.solid('concrete', px, 0.17 + rng() * 0.02, pz, w, 0.05, w, {
            rotY: (rng() - 0.5) * 0.12, color: vary(rng, 0x9e968a, 0.07),
            uOff: rng() * 6, vOff: rng() * 6,
            collide: false, shadow: 'cast',
          });
        }
        if (rng() < 0.35) P.plant(this.b, rng, px, 0.16, pz, 0.35 + rng() * 0.4);
      }
    }
  }

  /**
   * Wear laid over a finished road: patching, potholes, tracks and spillage.
   *
   * A carriageway is the largest single surface a player looks at and the one with
   * the least excuse for being uniform. All of it is 2 cm flat geometry, so it costs
   * nothing and it is what stops the tiling pattern being the most interesting thing
   * in the frame.
   *
   * Every family here already sat on its own rung above the carriageway — floor,
   * tracks, patch, joint, rim, in the order they occur on a real road. What it did
   * not have was a rung *within* a family, so two overlapping potholes, or two runs
   * of tyre track laid down the same lane, shared a depth plane and swapped as the
   * camera moved. `sub` is that missing rung: half an `overlayStep`, alternating per
   * instance, which keeps the whole stack inside 23 mm — nothing here is allowed to
   * grow a lip you could trip over — while never letting two pieces of the same
   * family land on the same plane.
   */
  _roadWear(x0, x1, z0, z1, o = {}) {
    const rng = this.rng;
    const b = this.b;
    const y = (o.y ?? TUNING.streetY) + 0.006;
    const sub = (i) => (i & 1) * (TUNING.overlayStep / 2);
    const area = (x1 - x0) * (z1 - z0);
    // Patch repairs. Two or three overlapping lobes rather than one rectangle, and
    // only a little darker than the road: a hard black rectangle lying on tarmac
    // reads as a decal, which is the same mistake the old wall patches made.
    for (let i = 0; i < Math.round(area / 70); i++) {
      const px = x0 + rng() * (x1 - x0);
      const pz = z0 + rng() * (z1 - z0);
      const w = 1.2 + rng() * 2.8;
      const d = 1.0 + rng() * 2.2;
      const rot = (rng() - 0.5) * 0.4;
      const col = vary(rng, 0x6f6a62, 0.08, 0.02, { lum: 0.8 });
      for (let k = 0; k < 2 + Math.floor(rng() * 2); k++) {
        b.solid('asphalt', px + (rng() - 0.5) * w * 0.5, y + k * 0.002 + sub(i), pz + (rng() - 0.5) * d * 0.5,
          w * (0.6 + rng() * 0.5), 0.014, d * (0.6 + rng() * 0.5), {
            tile: 1.2, rotY: rot + (rng() - 0.5) * 0.5, color: col,
            collide: false, shadow: 'none', uOff: rng() * 8, vOff: rng() * 8,
          });
      }
      // The sealed joint over one edge of it.
      // The two joints cross at the patch corner, so they take opposite sub-rungs.
      for (const [j, [ox, oz, bw, bd]] of [[0, -d / 2, w, 0.07], [-w / 2, 0, 0.07, d]].entries()) {
        b.solid('asphalt', px + ox, y + 0.008 + sub(i + j), pz + oz, bw, 0.012, bd, {
          rotY: rot, color: shade(0x403b34, 0.42), collide: false, shadow: 'none',
        });
      }
    }
    // Potholes: a dark floor with a lighter broken rim, which is the only way to read
    // a hole on a surface you are not allowed to cut a hole in.
    for (let i = 0; i < Math.round(area / 140); i++) {
      const px = x0 + rng() * (x1 - x0);
      const pz = z0 + rng() * (z1 - z0);
      const r = 0.28 + rng() * 0.42;
      b.solid('asphalt', px, y - 0.004 + sub(i), pz, r * 2, 0.014, r * 1.6, {
        rotY: rng() * 3, uOff: rng() * 8, vOff: rng() * 8, color: shade(0x4a443a, 0.44), collide: false, shadow: 'none',
      });
      // Five rim stones around one hole, placed on a circle, so they overlap each
      // other far more often than two potholes do — they alternate rung by stone.
      for (let k = 0; k < 5; k++) {
        const a = rng() * Math.PI * 2;
        const rr = r * (0.85 + rng() * 0.35);
        b.solid('asphalt', px + Math.cos(a) * rr, y + 0.006 + sub(k), pz + Math.sin(a) * rr, 0.18 + rng() * 0.2, 0.02, 0.14 + rng() * 0.16, {
          rotY: rng() * 3, uOff: rng() * 8, vOff: rng() * 8,
          color: vary(rng, 0x9a9184, 0.1), collide: false, shadow: 'none',
        });
      }
    }
    // Tyre tracks: two faint parallel darker bands where everything has driven.
    const alongX = x1 - x0 > z1 - z0;
    for (let i = 0; i < Math.round((alongX ? x1 - x0 : z1 - z0) / 26); i++) {
      const c0 = alongX ? z0 + rng() * (z1 - z0) : x0 + rng() * (x1 - x0);
      for (const s of [-1, 1]) {
        const c = c0 + s * 0.85;
        if (alongX) {
          b.solid('asphalt', (x0 + x1) / 2, y - 0.002 + sub(i), c, x1 - x0, 0.012, 0.34, {
            color: shade(0x4a453e, 0.5), collide: false, shadow: 'none', uOff: rng() * 8, vOff: rng() * 8,
          });
        } else {
          b.solid('asphalt', c, y - 0.002 + sub(i), (z0 + z1) / 2, 0.34, 0.012, z1 - z0, {
            color: shade(0x4a453e, 0.5), collide: false, shadow: 'none', uOff: rng() * 8, vOff: rng() * 8,
          });
        }
      }
    }
    // Oil drips where things have stood.
    for (let i = 0; i < Math.round(area / 130); i++) {
      P.groundStain(b, rng, x0 + rng() * (x1 - x0), z0 + rng() * (z1 - z0), 0.4 + rng() * 0.6, { y: TUNING.streetY, lum: 0.5 });
    }
  }

  /**
   * Standard hollow block: four walls with punched openings, a roof slab and a
   * parapet. Most of the map's silhouette is made of these.
   */
  _shell(o) {
    const { x0, x1, z0, z1, h, surface, color, thick = 0.6, west = [], east = [], north = [], south = [] } = o;
    const b = this.b;
    const rng = this.rng;
    // `rng` is what switches on opening trim inside P.wall — lintel, reveal,
    // threshold, a capped 2.1 m head and a leaf. Every shell on the map gets it.
    P.wall(b, surface, { axis: 'z', from: z0, to: z1, at: x0 + thick / 2, y0: 0, y1: h, thick, color, rng, openings: west });
    P.wall(b, surface, { axis: 'z', from: z0, to: z1, at: x1 - thick / 2, y0: 0, y1: h, thick, color, rng, openings: east });
    P.wall(b, surface, { axis: 'x', from: x0, to: x1, at: z0 + thick / 2, y0: 0, y1: h, thick, color, rng, openings: north });
    P.wall(b, surface, { axis: 'x', from: x0, to: x1, at: z1 - thick / 2, y0: 0, y1: h, thick, color, rng, openings: south });
    if (o.floor !== false) this._slab('concrete', x0, x1, z0, z1, TUNING.floorY, { tile: 3.0 });
    const rt = h + 0.3;
    this.facades.push({ x0, x1, z0, z1, h, surface });
    const rx0 = x0 - 0.35, rx1 = x1 + 0.35, rz0 = z0 - 0.35, rz1 = z1 + 0.35;
    const hole = o.roofHole;
    if (hole) {
      // A breach, laid out as the four slabs left around it. Rubble inside a building
      // has to have somewhere to have fallen *from*; without this the mound below is
      // an object someone placed, and the player can feel that instantly.
      this._box('concrete', rx0, rx1, h, rt, rz0, hole.z0, { color: tint(0x968e80) });
      this._box('concrete', rx0, rx1, h, rt, hole.z1, rz1, { color: tint(0x968e80) });
      this._box('concrete', rx0, hole.x0, h, rt, hole.z0, hole.z1, { color: tint(0x968e80) });
      this._box('concrete', hole.x1, rx1, h, rt, hole.z0, hole.z1, { color: tint(0x968e80) });
      this._roofBreachEdge(hole, h, rt);
    } else {
      this._box('concrete', rx0, rx1, h, rt, rz0, rz1, { color: tint(0x968e80) });
    }
    this._parapet(x0 - 0.35, x1 + 0.35, z0 - 0.35, z1 + 0.35, rt, o.parapet ?? 0.88, { color: tint(0x9a9285), skip: o.skip ?? [] });
    // Covering, fall, upstand flashing and an outlet on every flat roof that is big
    // enough for the player to stand on and look across.
    if (o.deck !== false && (x1 - x0) > 4 && (z1 - z0) > 4) {
      P.roofDeck(b, rng, x0 - 0.2, x1 + 0.2, z0 - 0.2, z1 + 0.2, rt, {
        outletX: x0 + (x1 - x0) * 0.32, outletZ: z1 - 1.1, upstand: 0.24,
      });
    }
    return rt;
  }

  /**
   * The torn edge of a roof breach: snapped slab, bent reinforcement hanging into
   * the void, and a few joists still bridging it. A clean rectangular hole reads as
   * a hatch, which is the opposite of the story.
   */
  _roofBreachEdge(hole, h, rt) {
    const rng = this.rng;
    const b = this.b;
    const edges = [
      ['x', hole.x0, hole.x1, hole.z0, -1], ['x', hole.x0, hole.x1, hole.z1, 1],
      ['z', hole.z0, hole.z1, hole.x0, -1], ['z', hole.z0, hole.z1, hole.x1, 1],
    ];
    for (const [axis, a0, a1, at, sgn] of edges) {
      const n = Math.max(3, Math.round((a1 - a0) / 0.7));
      for (let i = 0; i < n; i++) {
        const t = a0 + ((a1 - a0) * (i + 0.5)) / n;
        const over = (0.1 + rng() * 0.5) * -sgn;
        const w = (a1 - a0) / n * (0.7 + rng() * 0.5);
        const px = axis === 'x' ? t : at + over;
        const pz = axis === 'x' ? at + over : t;
        P.angled(b, 'concrete', px, h + 0.15 + (rng() - 0.5) * 0.12, pz,
          axis === 'x' ? w : Math.abs(over) + 0.3, 0.22, axis === 'x' ? Math.abs(over) + 0.3 : w,
          (rng() - 0.5) * 0.5, rng() * 0.4, (rng() - 0.5) * 0.5,
          { color: vary(rng, 0x968e80, 0.12) });
        // Reinforcement dangling out of the broken slab.
        if (rng() < 0.45) {
          P.rod(b, 'metalPainted', px, h + 0.02, pz,
            (rng() - 0.5) * 1.2, -1, (rng() - 0.5) * 1.2, 0.4 + rng() * 0.6, 0.011,
            shade(0x744a2e, 0.5, 0.95), { seg: 4 });
        }
      }
    }
    // Two joists still spanning the void, and the plaster hanging off them.
    for (let k = 0; k < 2; k++) {
      const t = hole.x0 + (hole.x1 - hole.x0) * (0.28 + k * 0.42);
      P.angled(b, 'wood', t, h - 0.02, (hole.z0 + hole.z1) / 2, 0.13, 0.2, (hole.z1 - hole.z0) * 1.02,
        (rng() - 0.5) * 0.12, 0, 0, { color: vary(this.rng, 0x7a5f42, 0.14) });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  ground plane and roads                                             */
  /* ------------------------------------------------------------------ */

  _ground() {
    const rng = this.rng;
    const b = this.b;
    // Four quadrants with different UV origins: one 130 m plane at a single offset
    // strobes its crack pattern all the way to the horizon.
    //
    // The repeat itself also had to come down. The dirt sampler lays 40 pebbles
    // across a tile, which sizes honestly at 1.4 m — correct for a 35 mm pebble and
    // hopeless for a 100 m plane, because a 1.4 m crack network laid end to end is a
    // grid the eye locks onto instantly. `groundTile` trades a slightly coarse pebble
    // for a repeat that only shows up under your feet.
    for (const [qx, qz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      this._slab('dirt', qx < 0 ? -H - 14 : 0, qx < 0 ? 0 : H + 14, qz < 0 ? -H - 14 : 0, qz < 0 ? 0 : H + 14, 0, {
        matId: 'dirt', uOff: rng() * 8, vOff: rng() * 8,
      });
    }
    // And on top of it, large soft patches at random rotations and UV offsets. This
    // is the standard cure for a tiled plane and there is no substitute: no amount of
    // scattered clutter hides a repeat, because the repeat is in the *texture*, and
    // only something that breaks the texture's own frame can interrupt it.
    //
    // The height ladder is not decoration. These are 12 m skins scattered over a
    // 120 m square, so they overlap each other constantly, and 320 of them will never
    // all fit on their own depth plane inside the 18 mm of headroom the asphalt
    // leaves. What matters is not that no two ever share a rung — it is that the two
    // that do are *the same colour*, because the amplitude of a depth swap is the
    // difference between the two things swapping.
    //
    // So the rung is chosen by the tint first and the index second. Sixteen rungs:
    // four tints, four sub-rungs each. Two patches on the same rung are guaranteed to
    // come from the same hex and differ only by `vary`'s few per cent, which is a swap
    // nobody can see. The old ladder did the opposite — three rungs, tint independent,
    // so the palest patch and the darkest one shared a plane about a third of the time.
    const HEX = [0xb8ae98, 0x9c9280, 0xa89d84, 0x8f887a];
    const skin = 0.016;
    for (let i = 0; i < 320; i++) {
      const px = (rng() - 0.5) * 2 * (H + 8);
      const pz = (rng() - 0.5) * 2 * (H + 8);
      const w = 3.5 + rng() * 15;
      const hi = Math.floor(rng() * HEX.length);
      const hex = HEX[hi];
      // `- skin / 2` because the rung names the patch's *top* face, which is the only
      // face the depth test ever compares; the rest of the box is buried.
      const top = TUNING.overlayLift + (hi * 4 + (i % 4)) * TUNING.overlayRung;
      b.solid('dirt', px, top - skin / 2, pz, w, skin, w * (0.5 + rng() * 0.9), {
        rotY: rng() * Math.PI, color: vary(rng, hex, 0.08, 0.02, { sat: 0.5 }),
        collide: false, shadow: 'none', uOff: rng() * 10, vOff: rng() * 10,
      });
    }

    const roads = [
      [-9, 9, 4, H + 2],          // market street, south leg
      [-17, 17, -11, 5],          // plaza
      [-3, 15, -40, -10],         // north leg, jogged east
      [-H - 2, H + 2, 24, 31],    // south cross street
      [-H - 2, H + 2, -39, -32],  // north cross street
      [16, 23, -32, 24],          // east lane
      [-26, -17, -32, 24],        // west alley
    ];
    for (const [x0, x1, z0, z1] of roads) {
      this._slab('asphalt', x0, x1, z0, z1, TUNING.streetY, { matId: 'concrete', color: tint(0xb9b1a2, { lum: 1.14 }) });
    }
    for (const [x0, x1, z0, z1] of roads) this._roadWear(x0, x1, z0, z1, {});

    // Kerbs define the road edges and give the eye leading lines toward the plaza.
    // `side`/`foot` put a raised footway behind the kerb wherever there is room for
    // one; the plaza and north-leg edges abut another carriageway, so they get the
    // channel and the stone but no pavement.
    this._kerb(-9.3, -9, 4, 24, { side: -1, foot: 1.6 });
    this._kerb(9, 9.3, 4, 24, { side: 1, foot: 1.2 });
    this._kerb(-9.3, -9, 31, H, { side: -1, foot: 1.6 });
    this._kerb(9, 9.3, 31, H, { side: 1, foot: 1.6 });
    this._kerb(-17.3, -17, -11, 5, { side: -1 });
    this._kerb(17, 17.3, -11, 5, { side: 1 });
    this._kerb(-3.3, -3, -32, -11, { side: -1, foot: 1.4 });
    this._kerb(15, 15.3, -32, -11, { side: 1 });
  }

  /* ------------------------------------------------------------------ */
  /*  compound wall                                                      */
  /* ------------------------------------------------------------------ */

  _perimeter() {
    const rng = this.rng;
    const b = this.b;
    const wallH = TUNING.wallHeight;
    for (const s of [{ axis: 'z', at: -H }, { axis: 'z', at: H }, { axis: 'x', at: -H }, { axis: 'x', at: H }]) {
      const segs = 9;
      for (let i = 0; i < segs; i++) {
        // Segments overlap by 0.3 m: the jitter between them reads as separate wall
        // sections, but leaves no slit for a bullet or a sightline to slip through.
        const t0 = -H - 2 + (i * (2 * H + 4)) / segs;
        const t1 = -H - 2 + ((i + 1) * (2 * H + 4)) / segs + 0.3;
        const a = s.at + (rng() - 0.5) * 0.9;
        const h = wallH + (rng() - 0.5) * 0.9;
        const col = vary(rng, 0x9c9382, 0.09);
        if (s.axis === 'z') {
          this._box('concrete', t0, t1, 0, h, a - 0.3, a + 0.3, { color: col });
          this._box('concrete', t0, t1, h, h + 0.22, a - 0.42, a + 0.42, { color: col.clone().multiplyScalar(1.06), collide: false, shadow: 'cast' });
          for (let px = t0 + 1.5; px < t1; px += 5.2) {
            this._box('concrete', px - 0.3, px + 0.3, 0, h + 0.55, a - 0.45, a + 0.45, { color: col.clone().multiplyScalar(0.94) });
          }
          b.collider(t0 - 0.4, 0, a - 0.6, t1 + 0.4, 22, a + 0.6, 'concrete'); // cap: nobody leaves the map
        } else {
          this._box('concrete', a - 0.3, a + 0.3, 0, h, t0, t1, { color: col });
          this._box('concrete', a - 0.42, a + 0.42, h, h + 0.22, t0, t1, { color: col.clone().multiplyScalar(1.06), collide: false, shadow: 'cast' });
          for (let pz = t0 + 1.5; pz < t1; pz += 5.2) {
            this._box('concrete', a - 0.45, a + 0.45, 0, h + 0.55, pz - 0.3, pz + 0.3, { color: col.clone().multiplyScalar(0.94) });
          }
          b.collider(a - 0.6, 0, t0 - 0.4, a + 0.6, 22, t1 + 0.4, 'concrete');
        }
      }
    }

    for (let i = 0; i < 11; i++) {
      const t = -H + 7 + i * 9.0;
      P.streetLamp(b, -H + 2.6, t, Math.PI / 2, { h: 6.0 });
      P.streetLamp(b, H - 2.6, t, -Math.PI / 2, { h: 6.0 });
    }

    // Gate at the south end of the market street — where the player came in.
    P.fence(b, -13, H - 1.6, 7, 'x', { h: 2.6 });
    P.fence(b, 13, H - 1.6, 7, 'x', { h: 2.6 });
    for (const sx of [-4.6, 4.6]) {
      this._box('metalPainted', sx - 0.16, sx + 0.16, 0, 5.2, H - 1.75, H - 1.45, { tile: 1.0, color: tint(0x6b6355) });
    }
    this._box('metalPainted', -5.2, 5.2, 4.7, 5.2, H - 1.75, H - 1.45, { tile: 1.0, color: tint(0x6b6355), collide: false, shadow: 'cast' });
    P.sign(b, rng, 0, 4.95, H - 1.95, 3.4, 0.7, Math.PI, 0x9a3a2a, { text: 'HALT' });
  }

  /* ------------------------------------------------------------------ */
  /*  WEST LANE — warehouse                                              */
  /* ------------------------------------------------------------------ */

  _warehouse() {
    const rng = this.rng;
    const b = this.b;
    const x0 = -45, x1 = -26, z0 = -4, z1 = 20;
    const roof = TUNING.warehouseRoof;
    const mezz = TUNING.warehouseMezz;
    const shell = tint(0x8e8a7c);

    this._slab('concrete', x0 - 0.4, x1 + 0.4, z0 - 0.4, z1 + 0.4, TUNING.floorY, { tile: 3.0 });
    this.facades.push({ x0, x1, z0, z1, h: roof, surface: 'corrugated' });

    // --- shell -------------------------------------------------------
    // No `tile` override: the corrugated texel density in Geometry is derived from a
    // real 76 mm cladding rib, and forcing 2.0 here stretched it to 330 mm, which is
    // the pitch of barn boarding. The whole point of TEXEL is not to second-guess it.
    //
    // The south door and the north door used to line up, and with the depot's north
    // door beyond them that made a 67 m shot from the container yard straight through
    // two buildings. The north door is offset 4.5 m east, which costs nothing in
    // circulation — both are still enormous — and removes the shot entirely.
    P.wall(b, 'corrugated', {
      axis: 'x', from: x0 - 0.2, to: x1 + 0.2, at: z1 - 0.2, y0: 0, y1: roof - 0.25, thick: 0.4,
      color: shell, matId: 'metal', rng, openings: [{ a0: -39, a1: -32, y0: 0, y1: 4.6 }],
    });
    P.wall(b, 'corrugated', {
      axis: 'x', from: x0 - 0.2, to: x1 + 0.2, at: z0 + 0.2, y0: 0, y1: roof - 0.25, thick: 0.4,
      color: shell, matId: 'metal', rng, openings: [{ a0: -35.0, a1: -28.5, y0: 0, y1: 4.6 }],
    });
    P.wall(b, 'corrugated', {
      axis: 'z', from: z0 - 0.2, to: z1 + 0.2, at: x0 + 0.2, y0: 0, y1: roof - 0.25, thick: 0.4,
      color: shell, matId: 'metal', rng, openings: [{ a0: 2, a1: 5, y0: 0, y1: 2.9 }],
    });
    P.wall(b, 'corrugated', {
      axis: 'z', from: z0 - 0.2, to: z1 + 0.2, at: x1 - 0.2, y0: 0, y1: roof - 0.25, thick: 0.4,
      color: shell, matId: 'metal', rng, openings: [{ a0: 6, a1: 11, y0: 0, y1: 4.0 }],
    });

    // Roof, split around the stair hatch in the south-west corner.
    const rc = tint(0x847f72);
    this._box('corrugated', x0 - 0.3, x1 + 0.3, roof - 0.25, roof, z0 - 0.3, 13.8, { color: rc, matId: 'metal' });
    this._box('corrugated', -42.9, x1 + 0.3, roof - 0.25, roof, 13.8, z1 + 0.3, { color: rc, matId: 'metal' });
    // Upstand, omitted on the east edge by the container stack so the mantle works.
    this._box('concrete', x0 - 0.35, x1 + 0.35, roof, roof + 0.3, z0 - 0.35, z0, { color: rc, cover: 'low' });
    this._box('concrete', x0 - 0.35, x1 + 0.35, roof, roof + 0.3, z1, z1 + 0.35, { color: rc, cover: 'low' });
    this._box('concrete', x0 - 0.35, x0, roof, roof + 0.3, z0, z1, { color: rc, cover: 'low' });
    this._box('concrete', x1, x1 + 0.35, roof, roof + 0.3, 6, z1, { color: rc, cover: 'low' });

    // Sheet laps and a roof walkway, so the deck is not one unbroken plane.
    for (let i = 0; i < 12; i++) {
      const lx = x0 + 0.6 + i * ((x1 - x0 - 1.2) / 11);
      this._box('metalPainted', lx - 0.05, lx + 0.05, roof, roof + 0.07, z0 - 0.3, z1 + 0.3, {
        tile: 0.5, color: rc.clone().multiplyScalar(0.9), collide: false, shadow: 'none',
      });
    }
    this._box('metalPainted', -35.4, -34.2, roof, roof + 0.04, z0, z1, {
      tile: 1.4, color: tint(0x9a9184), collide: false, shadow: 'none',
    });

    // Saw-tooth roof monitors. They were 4 cm slabs of glass lying flat on the deck
    // with no upstand and no pitch, which reads as tarpaulin — a rooflight without a
    // kerb is not a rooflight, it is a puddle. Now they have the kerb, the pitch, the
    // glazing bars and the solid back that make the roofline saw-toothed at all.
    // Three bays per row rather than one 16 m sheet: a monitor is built in structural
    // bays with a solid pier between them, and a single unbroken pane of that size
    // reads as a swimming pool on the roof from every elevated position on the map.
    for (let i = 0; i < 4; i++) {
      const sz = z0 + 3 + i * 5.2;
      for (let k = 0; k < 3; k++) {
        const bx0 = x0 + 1.6 + k * 5.35;
        P.sawtoothMonitor(b, rng, bx0, bx0 + 4.6, sz, roof, { depth: 1.9, rise: 1.15, kerb: 0.32 });
      }
    }

    // --- mezzanine ---------------------------------------------------
    this._box('concrete', x0, -38.5, mezz - 0.28, mezz, 6, z1 - 0.3, { color: tint(0x968e80) });
    for (let i = 0; i < 5; i++) {
      const pz = 6.8 + i * 3.1;
      this._box('metalPainted', -38.9, -38.5, 0, mezz - 0.28, pz - 0.2, pz + 0.2, { tile: 1.0, color: tint(0x6d6558) });
    }
    P.railing(b, { x: -38.6, y: mezz, z: 13, len: 13.4, axis: 'z', color: tint(0x6d6558) });
    b.markCover(-38.6, 13, 0.4, 6.7, mezz + 1.0, 'high', mezz);

    // Ground -> mezzanine, then mezzanine -> roof hatch. 0.185 rise / 0.28 going —
    // see Props.TUNING. The landings are unchanged; the flights start further along.
    P.stairs(b, { x: -41.5, y: 0, z: 0.96, dirZ: 1, width: 1.8, steps: 18, rise: mezz / 18, run: 0.28, surface: 'concrete', color: tint(0x8f8778) });
    P.stairs(b, { x: -44.0, y: mezz, z: 18.66, dirZ: -1, width: 1.7, steps: 17, rise: (roof - mezz) / 17, run: 0.28, surface: 'metalPainted', color: tint(0x6d6558) });
    P.railing(b, { x: -42.75, y: roof, z: 16.28, len: 4.76, axis: 'z', color: tint(0x6d6558) });

    // --- interior ----------------------------------------------------
    // Aisles and a marked-out loading zone: the floor of an 19 x 24 m shed is one of
    // the biggest planes on the map and it was a blank slab.
    P.floorMarking(b, rng, -33.5, 8, 4.6, 22, 0, { y: TUNING.floorY });
    P.floorMarking(b, rng, -30.0, 16.0, 3.4, 3.4, 0.1, { y: TUNING.floorY, kind: 'hatch' });
    P.groundStain(b, rng, -30.5, 15.5, 1.6, { y: TUNING.floorY });
    P.groundStain(b, rng, -36.5, 5.0, 1.2, { y: TUNING.floorY });
    P.dockNumber(b, rng, -36.5, 2.6, z1 - 0.44, Math.PI, 2, { size: 1.0 });
    // Purlins under the deck: the ceiling was a single flat plane, and it is what the
    // player looks at every time they check the mezzanine.
    P.purlins(b, x0 + 0.5, x1 - 0.5, z0 + 0.5, z1 - 0.5, roof - 0.28, { along: 'x', pitch: 2.1 });

    P.shelfRack(b, rng, -36, 1.5, 10.5, Math.PI / 2, { h: 4.2 });
    P.shelfRack(b, rng, -31, 1.5, 10.5, Math.PI / 2, { h: 4.2 });
    P.shelfRack(b, rng, -36, 16.5, 6.5, Math.PI / 2, { h: 4.2 });
    P.forklift(b, rng, -30.5, 15.5, 0.9);
    // Cross-aisle mass. With the doors offset the through-shot is already gone, but
    // 24 m of clear floor between them is still a bowling alley; this makes the route
    // through the building a jog, and it is deliberately *not* another container.
    P.concretePipes(b, rng, -33.0, 9.2, 0.06, { r: 0.52, len: 2.6, rows: 2, perRow: 3 });
    P.cableReel(b, rng, -36.4, 13.8, 1.2, { r: 0.95, w: 0.85 });
    P.brickPallet(b, rng, -37.4, 11.6, 0.3, {});
    P.brickPallet(b, rng, -38.5, 12.5, -0.2, { rows: 3 });
    P.workBench(b, rng, -43.6, 6.4, 3.2, Math.PI / 2, {});
    P.toolBoard(b, rng, x0 + 0.45, 1.7, 6.4, 1.6, 0.9, Math.PI / 2);
    P.palletStack(b, rng, -34, 17.5, 5, { rotY: 0.2 });
    P.palletStack(b, rng, -32.4, 17.6, 3, { rotY: -0.35 });
    P.crate(b, rng, -28.8, 0, 4.5, 1.5, 1.5, 1.2, { rotY: 0.1, cover: 'high' });
    P.crate(b, rng, -28.8, 1.5, 4.5, 1.1, 1.0, 1.0, { rotY: -0.3 });
    P.crate(b, rng, -28.6, 0, 6.6, 1.3, 1.05, 1.1, { rotY: 0.5, cover: 'low' });
    for (const [dx, dz] of [[-42, 3.5], [-41.2, 4.4], [-42.3, 5.2], [-28, 12.5], [-28.6, 13.6]]) P.drum(b, rng, dx, dz, {});
    P.gasBottles(b, rng, -29.4, 9.5, 3, { rotY: Math.PI / 2 });
    P.generator(b, rng, -43.2, 11.5, Math.PI / 2);
    P.tireStack(b, rng, -30.2, 18.4, 5);
    P.crate(b, rng, -42, mezz, 9, 1.4, 1.2, 1.2, { rotY: 0.2, cover: 'high' });
    P.crate(b, rng, -42.2, mezz, 11, 1.2, 0.9, 1.0, { rotY: -0.2, cover: 'low' });
    P.palletStack(b, rng, -40.2, 17.6, 4, { rotY: 0.1 });

    // Trusses and hanging lamps: vertical interest inside a big void.
    for (let i = 0; i < 5; i++) {
      const tz = z0 + 2.5 + i * 4.5;
      this._box('metalPainted', x0 + 0.4, x1 - 0.4, roof - 0.72, roof - 0.6, tz - 0.09, tz + 0.09, {
        tile: 2.0, color: tint(0x5e5750), collide: false, shadow: 'cast',
      });
      for (const lx of [-40, -31]) {
        P.tube(b, 'metalPainted', lx, roof - 1.15, tz, 0.02, 0.9, 0, 0, 0, { color: tint(0x2a2724), shadow: 'none' });
        b.add('metalPainted', cylGeo(0.3, 0.12, 0.24, 10, 0.5, true), trs(lx, roof - 1.68, tz), tint(0x7a6f5a), 'none');
        this._glow(lx, roof - 1.78, tz, 0.16, 0.1, 0.16, 0, 0xffc27a, 3.2);
      }
    }

    P.sign(b, rng, -35.5, 5.4, z1 + 0.25, 9.0, 1.5, 0, 0x2f4f60, { text: 'DUST YARD STORES' });
    P.sign(b, rng, -29.5, 3.0, z1 + 0.25, 2.2, 1.0, 0, 0x8a5a20, { text: 'BAY 2' });
    P.pipeRun(b, -35, 5.0, z1 + 0.38, 16, 'x', { r: 0.11, color: tint(0x5f6a5a) });
    P.pipeRun(b, x1 + 0.34, 4.6, 13, 12, 'z', { r: 0.09 });

    /* --- the 24 m east elevation --------------------------------------------
       This face is what the whole west alley looks at, and it carried one door, one
       pipe and a handful of decals over 24 m by 6 m. Everything below is the kit
       that any real industrial elevation has and no blockout ever does: the wall has
       to *land* on the ground, the roof has to *shed* into something, and there has
       to be a way up, a way for air out, and a number on the bay. */
    const eFace = { axis: 'z', at: x1, from: z0, to: z1, rotY: Math.PI / 2 };
    P.basePlinth(b, rng, eFace, { h: 0.46, out: 0.13, hex: 0x8d8578 });
    P.eavesGutter(b, rng, eFace, roof - 0.42, { out: 0.26, outlets: [2.2, 13.4, 22.0] });
    P.wallLadder(b, x1 + 0.02, 17.6, 0.5, roof + 0.5, Math.PI / 2, {});
    P.ventLouvre(b, rng, x1 + 0.05, 4.6, 3.4, 1.5, 1.1, Math.PI / 2, {});
    P.ventLouvre(b, rng, x1 + 0.05, 4.6, 16.2, 1.5, 1.1, Math.PI / 2, {});
    P.extractCowl(b, rng, x1 - 1.3, roof, 8.6, { r: 0.34, duct: 0.8 });
    P.extractCowl(b, rng, x1 - 1.3, roof, 11.4, { r: 0.28, duct: 0.6 });
    P.dockNumber(b, rng, x1 + 0.06, 5.2, 8.5, Math.PI / 2, 2, { size: 0.9 });
    for (const cz of [z0 - 0.1, z1 + 0.1]) P.cornerTrim(b, x1 + 0.06, cz, 0, roof - 0.2, {});
    // Panel failures on the cladding: rust runs, a mismatched replacement sheet, a
    // dent. Never render patches — this wall is steel.
    for (let i = 0; i < 9; i++) {
      P.panelStain(b, rng, x1 + 0.03, 1.2 + rng() * (roof - 2.4), z0 + 1.5 + rng() * (z1 - z0 - 3), Math.PI / 2, { drop: 2.2 });
    }
    P.conduitBox(b, rng, x1 + 0.02, 1.5, 6.0, Math.PI / 2);
    P.conduitBox(b, rng, x1 + 0.02, 1.35, 19.2, Math.PI / 2);

    // --- container stack: the climb to the roof ----------------------
    // ground -> pallets 1.05 -> container A 2.5 -> crate 3.7 -> container B 5.0 -> roof 6.4
    const sx = -24.4;
    // Two containers end to end carry a third on top, so the upper box sits back far
    // enough to leave a landing on the lower one for the intermediate crate step.
    P.container(b, rng, sx, 0.0, { rotY: Math.PI / 2, color: vary(rng, 0x6d7a52, 0.1) });
    P.container(b, rng, sx, -6.1, { rotY: Math.PI / 2 + 0.01, color: vary(rng, 0x46685c, 0.1) });
    P.container(b, rng, sx, -2.2, { rotY: Math.PI / 2, y: CH, color: vary(rng, 0x8a4a32, 0.1) });
    P.crate(b, rng, sx, CH, 1.7, 1.3, 1.2, 1.2, { rotY: 0.12, cover: 'high' });
    P.palletStack(b, rng, sx, 4.2, 8, { rotY: 0.25 });
    P.crate(b, rng, sx - 1.6, 0, 6.4, 1.2, 1.0, 1.1, { rotY: -0.2, cover: 'low' });

    // Gantry off the roof over the alley — an exposed but commanding firing step.
    this._box('metalPainted', -26.4, -21.6, roof - 0.2, roof, 14.4, 16.4, { tile: 1.6, color: tint(0x6b6356), matId: 'metal' });
    P.railing(b, { x: -24, y: roof, z: 14.5, len: 4.6, axis: 'x', color: tint(0x6b6356) });
    P.railing(b, { x: -24, y: roof, z: 16.3, len: 4.6, axis: 'x', color: tint(0x6b6356) });
    for (const gx of [-22.2, -25.6]) {
      P.tube(b, 'metalPainted', gx, roof / 2, 15.4, 0.08, roof, 0, 0, 0, { color: tint(0x5f594f) });
    }

    // Roof kit
    P.acUnit(b, rng, -41, roof, 5.5, 0.2);
    P.acUnit(b, rng, -39.4, roof, 6.6, -0.3);
    P.satDish(b, rng, -30, roof + 0.6, 3.4, 2.4, 0.55);
    b.add('metalPainted', cylGeo(1.1, 1.1, 1.6, 12, 1.0), trs(-33, roof + 0.8, 18), tint(0x8d8a7e), 'both');
    b.collider(-34.1, roof, 16.9, -31.9, roof + 1.6, 19.1, 'metal');
    b.markCover(-33, 18, 1.1, 1.1, roof + 1.6, 'high', roof);
    P.sandbags(b, rng, -32, 1.0, 1, 0, 3.4, 3, { y: roof });
    P.rubble(b, rng, this.gravel, -36, 10, 6, 40, { y: roof, hex: 0x7a7266, bounds: [x0 + 0.4, x1 - 0.4, z0 + 0.6, z1 - 0.6] });
  }

  /* ------------------------------------------------------------------ */
  /*  WEST LANE — container yard and depot                               */
  /* ------------------------------------------------------------------ */

  _westYard() {
    const rng = this.rng;
    const b = this.b;

    // Silos, a fuel tank and junk plug the strip between the warehouse and the west
    // wall, so the boundary never reads as a running corridor.
    for (const [cx, cz, r, sh] of [[-48, 10, 2.6, 12], [-48, 3.5, 2.6, 10.5]]) {
      b.post('concrete', cx, sh / 2, cz, r, sh, { seg: 16, color: vary(rng, 0xa39a88, 0.06) });
      b.add('metalPainted', cylGeo(r * 1.06, r * 0.4, 1.5, 16, 1.2), trs(cx, sh + 0.7, cz), tint(0x7d766a), 'both');
      for (let i = 0; i < 4; i++) {
        this._box('metalPainted', cx - r - 0.1, cx + r + 0.1, 2.2 + i * 2.4, 2.35 + i * 2.4, cz - r - 0.1, cz + r + 0.1, {
          tile: 1.0, color: tint(0x6b6458), collide: false, shadow: 'none',
        });
      }
    }
    b.add('metalPainted', cylGeo(1.7, 1.7, 7.5, 14, 1.6), trs(-48.5, 2.0, -8, 0, 0, Math.PI / 2), tint(0x77796a), 'both');
    b.collider(-52, 0, -9.7, -45, 3.7, -6.3, 'metal');
    b.markCover(-48.5, -8, 3.5, 1.7, 3.7, 'high');
    for (const cx of [-46.2, -50.8]) this._box('concrete', cx - 0.5, cx + 0.5, 0, 0.4, -9.6, -6.4, { color: tint(0x958d80) });

    /* Container yard, north-west: rows staggered so the lanes between them jink.
       Nothing here used to sit on sleepers, nothing was tilted, no doors were open
       and every stack was exactly two high and dead flush — which is what made twelve
       boxes read as one box drawn twelve times. Stacks now run one, two and three
       high, the ground rows sit on timber, and three of them are open. */
    const yard = [
      { x: -44.0, z: -12.0, r: 0, hex: 0x3f6070, sleepers: true },
      { x: -44.0, z: -18.4, r: 0, hex: 0x8a4a32, sleepers: true, doorsOpen: 1.35 },
      { x: -44.0, z: -12.0, r: 0, y: CH, hex: 0x9b8340 },
      { x: -44.15, z: -12.3, r: 0.02, y: CH * 2, hex: 0x6d7a52 },
      { x: -36.0, z: -9.0, r: Math.PI / 2, hex: 0x6d7a52, sleepers: true },
      { x: -36.0, z: -15.6, r: Math.PI / 2 + 0.03, hex: 0x5a4a55, sleepers: true },
      { x: -30.5, z: -20.0, r: 0.03, hex: 0x46685c, doorsOpen: 0.9 },
      { x: -38.0, z: -26.0, r: 0.06, hex: 0x7a3a34, sleepers: true },
      { x: -38.2, z: -26.15, r: 0.02, y: CH, hex: 0x3f6070 },
      { x: -46.5, z: -30.5, r: Math.PI / 2, hex: 0x9b8340, sleepers: true },
      { x: -30.0, z: -30.5, r: 0.02, hex: 0x6d7a52, doorsOpen: true },
      { x: -33.5, z: -23.5, r: Math.PI / 2, hex: 0x8a4a32, sleepers: true },
      { x: -41.5, z: -22.0, r: Math.PI / 2 + 0.02, hex: 0x46685c },
    ];
    for (const c of yard) {
      P.container(b, rng, c.x, c.z, {
        rotY: c.r, y: c.y ?? 0, color: vary(rng, c.hex, 0.1),
        sleepers: c.sleepers, doorsOpen: c.doorsOpen,
      });
    }
    // The rest of a yard: the handling plant, the spares, the spoil.
    P.cableReel(b, rng, -34.4, -12.6, 0.7, { r: 1.05, w: 0.95 });
    P.concretePipes(b, rng, -40.2, -16.0, Math.PI / 2 + 0.04, { r: 0.58, len: 2.8, rows: 3, perRow: 4 });
    P.oilTank(b, rng, -47.0, -24.0, 0.03, { r: 0.75, len: 3.2 });
    P.skipBin(b, rng, this.stones, -32.6, -27.2, 0.5, { len: 3.6 });
    P.brickPallet(b, rng, -35.2, -29.0, 0.25, { rows: 6 });
    P.groundStain(b, rng, -34.4, -14.0, 1.6, {});
    P.groundStain(b, rng, -42.0, -20.0, 1.3, {});

    // Climb chain onto the container tops (1.05 -> 2.5 -> 3.7 -> 5.0).
    P.container(b, rng, -31.0, -14.5, { rotY: 0.02, color: vary(rng, 0x5a4a55, 0.1) });
    P.container(b, rng, -31.0, -11.6, { rotY: 0.03, color: vary(rng, 0x46685c, 0.1) });
    P.container(b, rng, -31.0, -11.6, { rotY: 0.03, y: CH, color: vary(rng, 0x9b8340, 0.1) });
    P.palletStack(b, rng, -27.3, -14.5, 8, { rotY: 0.2 });
    P.crate(b, rng, -31.0, CH, -14.0, 1.3, 1.2, 1.2, { rotY: 0.15, cover: 'high' });
    P.crate(b, rng, -40.9, 0, -10.6, 1.3, 1.1, 1.2, { rotY: -0.2, cover: 'low' });

    P.generator(b, rng, -28.6, -8.2, 0);
    for (const [dx, dz] of [[-27.4, -6.5], [-26.9, -5.4], [-27.9, -5.2], [-45, -34], [-44.1, -33.2]]) P.drum(b, rng, dx, dz, {});
    P.drum(b, rng, -43.4, -35.4, { tipped: true });
    P.tireStack(b, rng, -29.4, -18.8, 4);
    P.jersey(b, -22.5, -8.5, Math.PI / 2);
    P.jersey(b, -22.5, -11, Math.PI / 2);
    P.jersey(b, -20.4, -6.4, 0);
    P.hesco(b, rng, -21.5, -18.0, 0, 1, 4, { h: 1.15 });
    P.rubble(b, rng, this.stones, -34, -17, 6, 40, {});

    // --- south-west depot -------------------------------------------
    const dx0 = -50, dx1 = -34, dz0 = 28, dz1 = 44;
    const dcol = vary(rng, 0x9c9080, 0.05);
    this._shell({
      x0: dx0, x1: dx1, z0: dz0, z1: dz1, h: 5.0, surface: 'concrete', color: dcol, thick: 0.5,
      north: [{ a0: -47, a1: -43, y0: 0, y1: 3.6 }, { a0: -40, a1: -36.5, y0: 0, y1: 3.6 }],
      east: [{ a0: 33, a1: 37, y0: 0, y1: 3.2 }],
      parapet: 0.6,
    });
    // Loading dock: a 1.1 m platform you mantle onto, ramped down at one end.
    this._box('concrete', dx0, dx1, 0, 1.1, dz0 - 3.2, dz0, { color: vary(rng, 0x8f8879, 0.05), cover: 'low' });
    for (let i = 0; i < 5; i++) {
      this._box('concrete', dx1 - 0.2 + i * 0.34, dx1 + 0.2 + i * 0.34, 0, 1.1 - i * 0.22, dz0 - 3.2, dz0, { color: dcol });
    }
    for (const cx of [-46.5, -38.5]) P.shutter(b, rng, cx, 1.9, dz0 + 0.05, 3.6, 3.4, 0);
    P.crate(b, rng, -47.5, 1.1, dz0 - 1.6, 1.4, 1.2, 1.2, { rotY: 0.2, cover: 'high' });
    P.palletStack(b, rng, -44.5, dz0 - 1.5, 5, { rotY: 0.1 });
    P.crate(b, rng, -36.5, 1.1, dz0 - 1.5, 1.3, 1.0, 1.1, { rotY: -0.3, cover: 'low' });
    P.forklift(b, rng, -41.5, dz0 - 5.5, -0.4);
    P.container(b, rng, -44, dz0 - 8.5, { rotY: 0.04, color: vary(rng, 0x5a4a55, 0.1), sleepers: true });
    P.timberStack(b, rng, -37, dz0 - 9.5, Math.PI / 2 + 0.03, { len: 4.4, w: 1.2, courses: 8 });
    P.bowser(b, rng, -34.6, dz0 - 6.4, Math.PI / 2 + 0.1, { r: 0.68, len: 2.4 });
    P.acUnit(b, rng, -44, 5.3, 34, 0.4);
    P.satDish(b, rng, -39, 5.9, 30.5, 2.0, 0.5);
    P.dockNumber(b, rng, -46.5, 4.3, dz0 - 0.02, Math.PI, 1, { size: 0.85 });
    P.dockNumber(b, rng, -38.5, 4.3, dz0 - 0.02, Math.PI, 1, { size: 0.85 });
    P.eavesGutter(b, rng, { axis: 'x', at: dz0, from: dx0, to: dx1, rotY: Math.PI }, 4.6, { out: 0.24, outlets: [3.0, 12.5] });

    /* --- the depot interior --------------------------------------------------
       This was a 16 x 16 x 5 m completely empty room with two doors in it — the
       single largest volume of nothing on the map, and one the player is invited
       into by a mantle chain. It is now a working goods store: bays down one side,
       a marked floor, structure in the ceiling and a reason for the lights. */
    P.purlins(b, dx0 + 0.7, dx1 - 0.7, dz0 + 0.7, dz1 - 0.7, 4.95, { along: 'z', pitch: 2.4, depth: 0.26 });
    P.floorMarking(b, rng, -42, 36, 5.0, 13, 0, { y: TUNING.floorY });
    P.floorMarking(b, rng, -47.0, 41.0, 4.0, 4.0, 0, { y: TUNING.floorY, kind: 'hatch' });
    for (let i = 0; i < 3; i++) P.battenLight(b, -42, 4.5, 31.5 + i * 4.4, 2.4, 0, {});
    P.shelfRack(b, rng, dx1 - 1.4, 36.0, 11.0, Math.PI / 2, { h: 4.2 });
    P.shelfRack(b, rng, dx0 + 1.6, 40.0, 6.5, Math.PI / 2, { h: 3.0 });
    P.workBench(b, rng, -44.5, dz1 - 1.4, 3.4, 0, {});
    P.toolBoard(b, rng, -44.5, 1.7, dz1 - 0.6, 1.8, 1.0, 0);
    P.brickPallet(b, rng, -47.0, 34.0, 0.2, {});
    P.brickPallet(b, rng, -47.4, 35.6, -0.15, { rows: 5 });
    P.cableReel(b, rng, -44.0, 33.0, 0.5, { r: 0.8, w: 0.7 });
    P.cableReel(b, rng, -45.6, 31.2, 0, { r: 0.7, w: 0.6, lying: true });
    P.oilTank(b, rng, -47.0, 38.6, Math.PI / 2, { r: 0.6, len: 2.4 });
    P.palletStack(b, rng, -41.0, 42.0, 6, { rotY: 0.2 });
    P.palletStack(b, rng, -39.2, 42.4, 4, { rotY: -0.3 });
    P.crate(b, rng, -37.5, 0, 41.0, 1.5, 1.4, 1.3, { rotY: 0.25, cover: 'high' });
    P.crate(b, rng, -37.4, 1.4, 41.1, 1.2, 1.0, 1.1, { rotY: -0.4 });
    P.gasBottles(b, rng, -35.6, 33.0, 3, { rotY: Math.PI / 2 });
    for (const [dx, dz] of [[-40.6, 30.4], [-39.8, 31.2], [-40.9, 31.6]]) P.drum(b, rng, dx, dz, {});
    P.groundStain(b, rng, -41.5, 34.5, 1.5, { y: TUNING.floorY });
    P.groundStain(b, rng, -45.5, 40.0, 1.1, { y: TUNING.floorY });
    P.rubble(b, rng, this.stones, -43, 39, 3.5, 22, { y: TUNING.floorY });
  }

  /* ------------------------------------------------------------------ */
  /*  the two blocks that wall in the market street                      */
  /* ------------------------------------------------------------------ */

  _marketBlocks() {
    const rng = this.rng;
    const b = this.b;
    const hgt = TUNING.marketBlockRoof;

    // West block: walled compound between the market and the west alley, with a
    // through-route so the centre and west lanes stay connected mid-map.
    const wc = vary(rng, 0xa08874, 0.05);
    const wTop = this._shell({
      x0: -16.5, x1: -10.6, z0: 6, z1: 24, h: hgt, surface: 'brick', color: wc,
      west: [{ a0: 8.5, a1: 11.5, y0: 0, y1: 2.9 }],
      east: [{ a0: 17.5, a1: 20.5, y0: 0, y1: 2.9 }],
      south: [{ a0: -14.5, a1: -12, y0: 0, y1: 2.6 }],
    });
    for (let i = 0; i < 4; i++) {
      P.wallAC(b, rng, -10.35, 3.4, 7.5 + i * 4.2, Math.PI / 2);
      P.window_(b, rng, -16.5, 3.1, 8 + i * 4.2, 1.1, 1.3, -Math.PI / 2, { shutter: true });
    }
    P.crate(b, rng, -14.5, 0, 9.5, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'low' });
    P.crate(b, rng, -12.5, 0, 20.5, 1.4, 1.3, 1.3, { rotY: -0.3, cover: 'high' });
    P.drum(b, rng, -15.4, 21.6, {});
    P.acUnit(b, rng, -14, wTop, 12, 0.3);
    P.satDish(b, rng, -12, wTop + 0.7, 18, 1.8, 0.5);
    P.laundry(b, rng, new THREE.Vector3(-16, wTop + 1.9, 9), new THREE.Vector3(-16, wTop + 1.8, 17));

    // East block: shopfronts onto the market, windows onto the east lane, with a
    // covered passage at mid-height so the lanes cross without an open junction.
    const ec = vary(rng, 0x9e8672, 0.05);
    const eTop = this._shell({ x0: 10.5, x1: 15.5, z0: 6, z1: 13, h: hgt, surface: 'brick', color: ec });
    this._shell({ x0: 10.5, x1: 15.5, z0: 16, z1: 24, h: hgt, surface: 'brick', color: ec, skip: ['e'] });
    // Roofed passage between the two masses.
    this._box('concrete', 10.15, 15.85, hgt, hgt + 0.3, 13, 16, { color: tint(0x968e80) });
    for (const pz of [13.3, 15.7]) {
      this._box('brick', 10.5, 15.5, 2.9, hgt, pz - 0.25, pz + 0.25, { color: ec });
    }
    /* These awnings pointed the wrong way. `rotY` on a wall-hung prop is the *outward
       normal*, and this face looks west, so it is -PI/2; at +PI/2 the whole canopy was
       built inside the building and all the player ever saw was the 0.4 m of it that
       stuck out past the shutter — which is a large part of why the map's awnings read
       as flat boards. Same bug on the shop row and the south-west terrace. */
    const shopNames = ['AL NASR', 'ZAHRA', 'SABAH', 'HABIB'];
    for (let i = 0; i < 4; i++) {
      const z = i < 2 ? 7.6 + i * 3.2 : 17.6 + (i - 2) * 3.2;
      P.shutter(b, rng, 10.15, 1.8, z, 2.6, 3.0, Math.PI / 2);
      P.awning(b, rng, 10.45, 3.7 + (i % 2) * 0.3, z, 2.9 + (i % 3) * 0.3, 1.7 + (i % 2) * 0.35, -Math.PI / 2, {});
      P.sign(b, rng, 10.2, 4.32, z, 2.5, 0.5, -Math.PI / 2, [0x2f4f60, 0x7a3a34, 0x8a5a20, 0x3a5a3a][i],
        { text: shopNames[i] });
      if (i === 1 || i === 3) P.hangingSign(b, rng, 10.5, 3.0, z + 1.7, -Math.PI / 2, shopNames[i], { w: 0.95, h: 0.44 });
    }
    P.sign(b, rng, 10.05, 4.15, 20.6, 3.0, 0.8, -Math.PI / 2, 0x2f6070, { text: 'MARKET' });
    this._glow(9.98, 4.15, 20.6, 0.05, 0.4, 2.5, Math.PI / 2, 0x6fd8ff, 4.0);
    // Goods stacked in the passage: it stays walkable, but not a rifle scope tunnel.
    P.crate(b, rng, 12.2, 0, 14.4, 1.4, 1.4, 1.4, { rotY: 0.12, cover: 'high' });
    P.crate(b, rng, 12.2, 1.4, 14.4, 1.2, 1.2, 1.2, { rotY: -0.2 });
    P.crate(b, rng, 14.4, 0, 15.1, 1.3, 1.3, 1.3, { rotY: 0.3, cover: 'high' });
    P.crate(b, rng, 14.4, 1.3, 15.1, 1.1, 1.1, 1.1, { rotY: 0.1 });
    P.drum(b, rng, 13.3, 13.6, {});
    for (let i = 0; i < 4; i++) {
      P.window_(b, rng, 15.5, 3.0, i < 2 ? 8 + i * 3.2 : 18 + (i - 2) * 3.2, 1.1, 1.4, Math.PI / 2, { shutter: true });
    }
    // Mantle chain onto the east block's roof from the east lane.
    P.container(b, rng, 16.9, 20.6, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x7a3a34, 0.1) });
    P.palletStack(b, rng, 16.9, 24.2, 8, { rotY: 0.2 });
    P.crate(b, rng, 16.5, CH, 18.6, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'high' });
    P.crate(b, rng, 19.2, 0, 17.4, 1.2, 1.0, 1.1, { rotY: -0.25, cover: 'low' });
    /* Off the roof the other way. The mantle chain arrives from the east lane, so the
       descent is put on the market side: goods stacked against the shopfront under the
       awnings, 2.44 m to their top and 3.3 m down to them from the parapet — inside the
       fall the player takes without damage, and outside the 1.55 m they could climb
       back up. The roof becomes a crossing between two lanes rather than a perch. */
    P.palletStack(b, rng, 9.1, 11.0, 8, { rotY: 0.18 });
    P.crate(b, rng, 9.1, 1.24, 11.0, 1.3, 1.2, 1.2, { rotY: -0.22, cover: 'high' });
    P.goodsPile(b, rng, 9.4, 2.44, 11.0, 0.4, 0.5);
    P.crate(b, rng, 9.3, 0, 13.0, 1.1, 0.9, 1.0, { rotY: 0.4, cover: 'low' });
    P.sandbags(b, rng, 12.0, 8.0, 1, 0, 3.0, 3, { y: eTop });
    P.laundry(b, rng, new THREE.Vector3(11.2, eTop + 1.9, 17.6), new THREE.Vector3(14.8, eTop + 1.8, 17.6));
    P.acUnit(b, rng, 13, eTop, 9, -0.2);
    P.satDish(b, rng, 13.5, eTop + 0.7, 21, 3.4, 0.5);
    P.rubble(b, rng, this.gravel, 13, 20, 3, 20, { y: eTop, hex: 0x8a8172, bounds: [10.9, 15.1, 16.4, 23.6] });
  }

  /* ------------------------------------------------------------------ */
  /*  CENTRE LANE — market street                                        */
  /* ------------------------------------------------------------------ */

  _centralStreet() {
    const rng = this.rng;
    const b = this.b;

    // --- spawn approach: the photograph ------------------------------
    // Foreground wrecks and sandbags, midground stalls and awnings, background
    // monument with the crane behind it.
    P.tWall(b, -7.4, 44.5, 0);
    P.tWall(b, -5.6, 44.5, 0);
    P.tWall(b, 5.6, 44.5, 0);
    P.tWall(b, 7.4, 44.5, 0);
    P.sandbags(b, rng, 4.8, 42.4, 1, 0, 3.4, 4, { posts: true });
    P.sandbags(b, rng, 6.6, 39.8, 0, 1, 2.6, 3, {});
    P.carWreck(b, rng, -5.6, 41.2, 0.22, { flat: true });
    P.carWreck(b, rng, -7.2, 34.5, -1.42, {});
    P.drum(b, rng, -3.2, 43.6, {});
    P.drum(b, rng, -2.6, 42.7, { tipped: true });
    P.jersey(b, 4.2, 45.6, 0.12);
    P.jersey(b, 6.4, 46.2, -0.05);
    P.jersey(b, -4.4, 45.9, -0.09);
    P.jersey(b, -6.7, 46.6, 0.06);
    P.drum(b, rng, 7.4, 45.0, {});
    P.drum(b, rng, 8.0, 44.2, { tipped: true });
    P.tireStack(b, rng, -7.8, 44.6, 4);
    P.rubble(b, rng, this.stones, -4, 45.5, 3.5, 26, {});
    P.plant(b, rng, 3.0, 0.02, 44.6, 1.0);

    // --- bus blocks the first straight --------------------------------
    P.busWreck(b, rng, -1.2, 34.2, 0.26);
    P.rubble(b, rng, this.stones, 3.5, 33, 3, 20, {});
    P.plant(b, rng, 5.2, 0.02, 32.4, 1.1);

    // --- market, both sides -------------------------------------------
    P.marketStall(b, rng, 6.6, 20.5, -Math.PI / 2 + 0.13, { w: 2.8 });
    P.marketStall(b, rng, 6.4, 16.0, -Math.PI / 2 - 0.22, { w: 2.2 });
    P.marketStall(b, rng, 6.9, 11.5, -Math.PI / 2 + 0.31, { w: 2.5 });
    P.marketStall(b, rng, -6.6, 13.5, Math.PI / 2 - 0.17, { w: 2.6 });
    P.marketStall(b, rng, -6.9, 9.0, Math.PI / 2 + 0.26, { w: 2.3 });
    /* The stall's own goods stop at its table edge, and a market does not: the ground
       either side of a stall is where the stock that would not fit goes. This is also
       the replacement for a shipping container that used to stand in the middle of the
       market street — 2.4 m of ISO steel between two vegetable stalls, which is the
       single clearest example of the habit this pass exists to break. */
    // Stacked shoulder-high, because it is also the thing that keeps the market street
    // from being a 62 m shooting lane now the container that used to stand here is gone.
    P.sackStack(b, rng, -6.0, 18.5, Math.PI / 2 + 0.04, { rows: 8, cols: 3, deep: 2 });
    P.sackStack(b, rng, 7.4, 13.7, -0.3, { rows: 4, cols: 2, deep: 1, tarp: false });
    for (const [gx, gz, gr] of [[6.2, 18.4, 0.4], [-6.0, 11.3, 1.9], [6.6, 9.3, -0.6], [-6.4, 15.1, 2.7]]) {
      P.goodsPile(b, rng, gx, 0.02, gz, gr, 0.62);
    }
    P.crate(b, rng, -6.2, 0, 16.4, 1.4, 1.2, 1.2, { rotY: 0.2, cover: 'high' });
    P.palletStack(b, rng, -7.4, 22.4, 6, { rotY: 0.25 });
    P.crate(b, rng, -8.0, 0, 23.6, 1.2, 1.0, 1.1, { rotY: -0.3, cover: 'low' });
    P.tireStack(b, rng, 8.0, 23.4, 4);
    P.concretePipes(b, rng, -8.7, 19.4, 1.62, { r: 0.5, len: 2.4, rows: 2, perRow: 3 });
    P.cableReel(b, rng, -8.8, 21.6, 0.4, { r: 0.9, w: 0.8 });
    P.drum(b, rng, 7.9, 25.0, {});
    // Chest-high here and no higher was leaving a 61 m diagonal open from the spawn
    // through the whole market once the container beside it came out; a bastion wall is
    // built to whatever height the position needs.
    P.hesco(b, rng, 2.6, 24.4, 1, 0, 4, { h: 1.95 });
    P.jersey(b, -2.6, 25.2, 0.05);
    P.jersey(b, -4.8, 25.2, -0.03);

    /* Awnings down the west footway. Head height, in the centre of the frame from the
       spawn, and each one now carries its own sag, folds, colour and wear — a run of
       four identical rectangles is what made this the most conspicuous prop on the map.
       The goods under them are what the awnings are shading. */
    /* Hung off the west block's own elevation rather than free-standing in the road,
       and interleaved with the air-conditioners already on that wall so the two do not
       occupy the same 30 cm of brick. The front poles land at the kerb, which is where
       a market awning's poles go. */
    for (const [az, aw, ad, adr] of [[9.6, 3.0, 1.6, 0.5], [13.8, 2.5, 1.5, 0.34], [18.0, 3.3, 1.75, 0.46], [22.2, 2.8, 1.55, 0.4]]) {
      P.awning(b, rng, -10.5, 3.72 + (az % 2) * 0.22, az, aw, ad, Math.PI / 2, { drop: adr });
      P.hangingGoods(b, rng, -9.5, 3.02, az, aw * 0.7, Math.PI / 2);
      P.goodsPile(b, rng, -9.6, 0.16, az + 0.9, rng() * 3, 0.58);
    }
    // The east footway is covered by the market block's own shopfront awnings; it gets
    // the stock that stands out in front of them.
    for (const [gz, gr] of [[10.4, 0.9], [12.6, 2.3], [19.2, 1.4], [21.6, 0.2]]) {
      P.goodsPile(b, rng, 9.7, 0.16, gz, gr, 0.58);
    }

    // --- north leg, jogged east around the tenement -------------------
    P.truckWreck(b, rng, 8.5, -24.0, 1.35, { hex: 0x6a6a52 });
    P.vanWreck(b, rng, 2.2, -18.5, 0.03, { hex: 0x8a8579 });
    P.container(b, rng, 11.0, -14.2, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x9b8340, 0.1) });
    P.hesco(b, rng, 5.5, -12.0, 1, 0, 5, { h: 1.95 });
    P.concretePipes(b, rng, 8.3, -14.2, 0.04, { r: 0.55, len: 2.6, rows: 3, perRow: 4 });
    P.sandbags(b, rng, 12.4, -28.0, 1, 0, 3.6, 5, { posts: true });
    P.skipBin(b, rng, this.stones, 11.8, -30.7, 0.06, { len: 3.6 });
    P.jersey(b, 0.4, -30.5, 0.02);
    P.jersey(b, 2.7, -30.5, -0.04);
    P.jersey(b, 5.0, -30.5, 0.03);
    P.rubble(b, rng, this.stones, 6, -20, 5, 40, {});
    P.drum(b, rng, 1.2, -13.4, {});
    P.drum(b, rng, 0.5, -12.5, {});
    P.drum(b, rng, 1.9, -12.3, {});
    P.plant(b, rng, -2.4, 0.02, -22.0, 1.3);
    P.plant(b, rng, 14.6, 0.02, -17.5, 0.9);

    // --- C1: the tenement mass that creates the jog -------------------
    const c0 = -24, c1 = -4, cz0 = -30, cz1 = -14, ch = 5.6;
    const bcol = vary(rng, 0xa08874, 0.05);
    const cTop = this._shell({
      x0: c0, x1: c1, z0: cz0, z1: cz1, h: ch, surface: 'brick', color: bcol,
      north: [{ a0: -18, a1: -15.4, y0: 0, y1: 2.6 }],
      south: [{ a0: -13, a1: -10.4, y0: 0, y1: 2.6 }],
      east: [],
      // The west wall lost its upper courses and the roof over them.
      west: [{ a0: -27.6, a1: -23.2, y0: 2.5, y1: ch }],
      parapet: 0.9,
      skip: ['e'], // the roof stair lands on this edge
      roofHole: { x0: c0 - 0.35, x1: -19.6, z0: -27.8, z1: -23.0 },
    });
    // What came through it. `dir` runs east, away from the wall it fell off, and the
    // mound doubles as the landing for a drop through the breach off the roof.
    P.rubbleMound(b, rng, this.stones, -21.2, -25.3, 2.9, 2.4, { dir: 0.1, brickHex: 0xa0563c });
    // Interior partition: a through-route with a dogleg, not an empty shed.
    this._box('brick', -16.5, -16.0, 0, ch, cz0 + 0.6, -22.5, { color: bcol });
    this._box('brick', -16.5, -9.0, 0, ch, -22.9, -22.4, { color: bcol });
    P.crate(b, rng, -20, 0, -17.5, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'low' });
    P.crate(b, rng, -12, 0, -27.0, 1.4, 1.3, 1.3, { rotY: -0.3, cover: 'high' });
    P.drum(b, rng, -7.5, -18.0, {});
    P.rubble(b, rng, this.stones, -14, -19, 3.5, 26, {});
    for (let i = 0; i < 4; i++) {
      P.window_(b, rng, c1, 3.1, cz0 + 3 + i * 3.6, 1.2, 1.5, Math.PI / 2, { shutter: true });
      P.window_(b, rng, c0, 3.1, cz0 + 3.5 + i * 3.6, 1.2, 1.5, -Math.PI / 2, { shutter: true });
    }
    // Roof stair on the east face, outside the shell.
    P.stairs(b, { x: -2.4, y: 0, z: -13.64, dirZ: -1, width: 1.8, steps: 32, rise: cTop / 32, run: 0.28, surface: 'concrete', color: tint(0x8f8778) });
    this._box('concrete', -4.6, -1.5, cTop - 0.3, cTop, -24.6, -22.6, { color: tint(0x968e80) });
    P.acUnit(b, rng, -20, cTop, -20, 0.3);
    P.satDish(b, rng, -8, cTop + 0.7, -28, 2.6, 0.6);
    P.laundry(b, rng, new THREE.Vector3(-22, cTop + 1.9, -28.5), new THREE.Vector3(-9, cTop + 1.8, -28.5));
    P.crate(b, rng, -18, cTop, -17.5, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'high' });
  }

  /* ------------------------------------------------------------------ */
  /*  CENTRE — plaza                                                     */
  /* ------------------------------------------------------------------ */

  _plaza() {
    const rng = this.rng;
    const b = this.b;

    // Raised monument plinth with steps: the vertical the spawn view frames.
    this._box('concrete', -4.2, 4.2, 0, 1.2, -7.2, 1.2, { color: vary(rng, 0xa8a08e, 0.04), cover: 'low' });
    for (let i = 0; i < 4; i++) {
      const g = 0.34 * (i + 1);
      this._box('concrete', -4.2 - g, 4.2 + g, 0, 1.2 - (i + 1) * 0.28, -7.2 - g, 1.2 + g, {
        tile: 2.0, color: vary(rng, 0xa8a08e, 0.04),
      });
    }
    this._box('concrete', -1.1, 1.1, 1.2, 2.0, -4.1, -1.9, { color: tint(0xb0a894), cover: 'high' });
    this._box('concrete', -0.72, 0.72, 2.0, 6.2, -3.72, -2.28, { color: tint(0xb5ad98) });
    this._box('concrete', -0.5, 0.5, 6.2, 7.3, -3.5, -2.5, { color: tint(0xb5ad98), collide: false, shadow: 'cast' });
    b.add('concrete', cylGeo(0.02, 0.44, 1.5, 4, 2.0), trs(0, 8.05, -3), tint(0xb5ad98), 'cast');
    P.rubble(b, rng, this.stones, 0, -1.4, 4.5, 34, {});
    P.plant(b, rng, 2.2, 1.2, -6.4, 1.0);
    P.plant(b, rng, -3.6, 0.02, 1.9, 1.2);

    // Sandbags and barriers ring the plinth: cover in the middle of open ground.
    P.sandbags(b, rng, -5.9, -1.0, 0, 1, 4.2, 4, { posts: true });
    P.sandbags(b, rng, 5.9, -4.5, 0, 1, 3.6, 3, {});
    P.hesco(b, rng, -2.0, 3.6, 1, 0, 4, { h: 1.15 });
    P.hesco(b, rng, 9.8, -9.2, 0, 1, 3, { h: 1.6 });
    // On the 71 m plaza diagonal, and tall enough that an eye at 1.67 cannot see
    // over it the way it saw over the car wreck that used to be the only thing here.
    P.kiosk(b, rng, 7.3, -6.0, { w: 3.0, d: 2.4, h: 2.9, rotY: -Math.PI / 2, hex: 0x9c9382 });
    P.groundStain(b, rng, 7.3, -3.9, 1.2, { y: TUNING.streetY });
    P.jersey(b, -9.5, -9.0, 0.02);
    P.jersey(b, -11.8, -9.0, -0.03);
    P.jersey(b, 11.0, 2.0, Math.PI / 2 + 0.03);
    P.jersey(b, 11.0, 4.3, Math.PI / 2);

    // Scaffold tower on the west side of the plaza — a 4 m firing platform reached
    // by a concrete stair; only the top level is decked so the stair can run through.
    const sh = P.scaffold(b, rng, -12.5, 1.5, 3.2, 2.6, 2, { levelHeight: 2.0, decks: [2] });
    P.stairs(b, { x: -12.5, y: 0, z: 8.98, dirZ: -1, width: 1.7, steps: 22, rise: sh / 22, run: 0.28, surface: 'concrete', color: tint(0x8f8778) });
    P.railing(b, { x: -12.5, y: sh, z: 0.3, len: 3.2, axis: 'x', color: tint(0x8e8478) });
    P.railing(b, { x: -14.05, y: sh, z: 1.5, len: 2.6, axis: 'z', color: tint(0x8e8478) });
    b.markCover(-12.5, 1.5, 1.6, 1.3, sh + 1.0, 'high', sh);
    P.crate(b, rng, -15.2, 0, 3.4, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'low' });
    P.cableReel(b, rng, -15.5, 1.6, 0.4, { r: 1.05, w: 0.95 });
    P.drum(b, rng, -15.0, 1.2, {});
    P.drum(b, rng, -15.7, 0.4, {});

    // Dead fountain on the east side, plus the bones of a market.
    b.add('concrete', cylGeo(2.6, 2.6, 0.52, 16, 2.0), trs(13.2, 0.26, -5.5), vary(rng, 0xa39b89, 0.05), 'both');
    b.collider(10.8, 0, -7.9, 15.6, 0.52, -3.1, 'concrete');
    b.markCover(13.2, -5.5, 2.4, 2.4, 0.52, 'low');
    b.add('concrete', cylGeo(0.34, 0.5, 0.9, 10, 1.0), trs(13.2, 0.75, -5.5), vary(rng, 0xa39b89, 0.05), 'both');
    P.rubble(b, rng, this.stones, 13.2, -5.5, 2.2, 18, {});
    P.marketStall(b, rng, 14.8, 0.8, Math.PI, { w: 2.4 });
    P.marketStall(b, rng, -13.4, -7.5, 0, { w: 2.4 });
    P.carWreck(b, rng, 6.2, -9.8, 2.9, {});
    P.streetLamp(b, 9.6, -2.0, -Math.PI / 2, { h: 7.0 });
    P.streetLamp(b, -9.6, -6.0, Math.PI / 2, { h: 7.0 });
    this._glow(9.6 - 0.85, 6.66, -2.0, 0.44, 0.06, 0.22, 0, 0xffc27a, 3.2);
    this._glow(-9.6 + 0.85, 6.66, -6.0, 0.44, 0.06, 0.22, 0, 0xffc27a, 3.2);
  }

  /* ------------------------------------------------------------------ */
  /*  EAST LANE — two-storey tenement                                    */
  /* ------------------------------------------------------------------ */

  _tenement() {
    const rng = this.rng;
    const b = this.b;
    const x0 = 24, x1 = 40, z0 = 8, z1 = 23;
    const f1 = TUNING.tenementFloor;
    const rf = TUNING.tenementRoof;
    const brickC = vary(rng, 0x9e8672, 0.05);
    const trimC = tint(0xa69e8c);

    // A worn cement screed with a surviving patch of the tiles somebody laid, not a
    // gleaming ceramic showroom. This is a home, and the floor was the loudest thing
    // in the room saying it was not.
    this._slab('concrete', x0, x1, z0, z1, TUNING.floorY, { tile: 2.4, color: vary(rng, 0x9a9285, 0.05) });
    this._box('tile', 28.0, 34.0, TUNING.floorY - 0.02, TUNING.floorY + 0.012, 16.0, 21.5, {
      color: vary(rng, 0xa8a294, 0.05), collide: false, shadow: 'receive',
    });
    this.facades.push({ x0, x1, z0, z1, h: rf, surface: 'brick' });

    // Ground storey — hollow, three ways in. The 3 m-wide openings are pedestrian
    // doors and get a 2.1 m head from P.opening; the 7 m one is a shop frontage and
    // keeps its height, but now reads as one because it has a proper goalpost.
    P.wall(b, 'brick', { axis: 'z', from: z0, to: z1, at: x0 + 0.3, y0: 0, y1: f1, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 12, a1: 19, y0: 0, y1: 2.9 }] });
    P.wall(b, 'brick', { axis: 'z', from: z0, to: z1, at: x1 - 0.3, y0: 0, y1: f1, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 10, a1: 13, y0: 0, y1: 2.4 }] });
    P.wall(b, 'brick', { axis: 'x', from: x0, to: x1, at: z0 + 0.3, y0: 0, y1: f1, thick: 0.6, color: brickC, rng, openings: [] });
    P.wall(b, 'brick', { axis: 'x', from: x0, to: x1, at: z1 - 0.3, y0: 0, y1: f1, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 30, a1: 33, y0: 0, y1: 2.4 }] });
    for (const px of [28.5, 33.5]) {
      this._box('concrete', px - 0.35, px + 0.35, 0, f1 - 0.3, 15.2, 15.9, { color: trimC, cover: 'high' });
    }

    // First-floor slab, holed for the stair (hole: x 36.8–39.5, z 16.6–23).
    this._box('concrete', x0 - 2.6, x1, f1 - 0.3, f1, z0, 15.7, { color: trimC });
    this._box('concrete', x0 - 2.6, 36.9, f1 - 0.3, f1, 15.7, z1, { color: trimC });
    this._box('concrete', 39.4, x1, f1 - 0.3, f1, 15.7, z1, { color: trimC });
    for (let i = 0; i < 5; i++) {
      const bz = z0 + 1.4 + i * 3.2;
      this._box('concrete', x0 - 2.4, x0, f1 - 0.62, f1 - 0.3, bz - 0.16, bz + 0.16, {
        tile: 0.8, color: trimC, collide: false, shadow: 'cast',
      });
    }
    P.railing(b, { x: x0 - 2.5, y: f1, z: 15.4, len: 14.2, axis: 'z', color: tint(0x6d6558) });
    b.markCover(x0 - 2.5, 15.4, 0.4, 7.1, f1 + 1.0, 'high', f1);

    // Interior stair, ground -> first floor (rises northward inside the hole).
    P.stairs(b, { x: 38.1, y: 0, z: 21.12, dirZ: -1, width: 1.8, steps: 19, rise: f1 / 19, run: 0.28, surface: 'concrete', color: trimC });

    // Upper storey.
    P.wall(b, 'brick', { axis: 'z', from: z0, to: z1, at: x0 + 0.3, y0: f1, y1: rf, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 13, a1: 17, y0: f1, y1: f1 + 2.7 }] });
    P.wall(b, 'brick', { axis: 'z', from: z0, to: z1, at: x1 - 0.3, y0: f1, y1: rf, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 10.5, a1: 12.5, y0: f1 + 0.9, y1: f1 + 2.5 }, { a0: 18, a1: 20, y0: f1 + 0.9, y1: f1 + 2.5 }] });
    P.wall(b, 'brick', { axis: 'x', from: x0, to: x1, at: z0 + 0.3, y0: f1, y1: rf, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 27, a1: 29, y0: f1 + 0.9, y1: f1 + 2.5 }, { a0: 33, a1: 35, y0: f1 + 0.9, y1: f1 + 2.5 }] });
    P.wall(b, 'brick', { axis: 'x', from: x0, to: x1, at: z1 - 0.3, y0: f1, y1: rf, thick: 0.6, color: brickC, rng,
      openings: [{ a0: 30, a1: 32, y0: f1 + 0.9, y1: f1 + 2.5 }] });
    this._box('brick', 32.0, 32.5, f1, rf, 8.6, 14.5, { color: brickC });
    this._box('brick', 32.0, 36.5, f1, rf, 14.5, 15.0, { color: brickC });

    // Stair, first floor -> roof, tucked against the west wall.
    P.stairs(b, { x: 26.0, y: f1, z: 9.98, dirZ: 1, width: 1.8, steps: 19, rise: (rf - f1) / 19, run: 0.28, surface: 'concrete', color: trimC });
    // Roof slab, holed for that stair (hole: x 24–27.6, z 9.0–14.4).
    this._box('concrete', 27.6, x1, rf - 0.3, rf, z0, z1, { color: trimC });
    this._box('concrete', x0, 27.7, rf - 0.3, rf, 15.3, z1, { color: trimC });
    this._box('concrete', x0, 27.7, rf - 0.3, rf, z0, 8.7, { color: trimC });
    this._parapet(x0 - 0.3, x1 + 0.3, z0 - 0.3, z1 + 0.3, rf, 0.95, { color: tint(0x9a9285) });

    // External stair from the cross street up to the balcony (lands at z = 23).
    P.stairs(b, { x: 22.6, y: 0, z: 28.32, dirZ: -1, width: 1.7, steps: 19, rise: f1 / 19, run: 0.28, surface: 'concrete', color: trimC });

    // Windows, wall kit and the layer that says "people live here".
    for (let i = 0; i < 4; i++) {
      P.window_(b, rng, x1, f1 + 1.7, z0 + 2.2 + i * 3.6, 1.1, 1.5, Math.PI / 2, { shutter: true });
      if (i === 1 || i === 2) this._glow(x1 + 0.12, f1 + 1.7, z0 + 2.2 + i * 3.6, 0.04, 1.3, 0.95, 0, 0xffc27a, 3.2);
    }
    for (let i = 0; i < 3; i++) P.wallAC(b, rng, x0 - 0.45, f1 + 2.6, z0 + 3.0 + i * 4.4, Math.PI);
    P.pipeRun(b, x1 + 0.38, 2.4, 15.5, 13, 'z', { r: 0.08 });
    P.pipeRun(b, x1 + 0.38, 4.4, 15.5, 13, 'z', { r: 0.06, color: tint(0x7a6a52) });
    P.laundry(b, rng, new THREE.Vector3(x0 - 2.3, f1 + 2.2, z0 + 2), new THREE.Vector3(x0 - 2.3, f1 + 2.1, z0 + 9));
    P.laundry(b, rng, new THREE.Vector3(x0 - 2.3, rf + 1.9, 10), new THREE.Vector3(x0 - 2.3, rf + 1.8, 18));

    // Roof kit — the skyline detail read from the whole east side of the map.
    // The tenement roof is the map's most-occupied elevated surface and it had no
    // felt, no fall, no outlet, no flashing and no aerial base — a concrete plate.
    P.roofDeck(b, rng, 27.8, x1 - 0.2, z0 + 0.2, z1 - 0.2, rf, { outletX: 37.6, outletZ: 21.6, upstand: 0.3, seam: 1.05 });
    P.tankStand(b, rng, 28.6, rf, 13.4, { h: 1.4, r: 0.66 });
    P.aerialBase(b, rng, 36.0, rf, 15.2, { h: 3.0 });
    P.acUnit(b, rng, 30, rf, 11.5, 0.2);
    P.acUnit(b, rng, 31.4, rf, 12.7, -0.4);
    P.satDish(b, rng, 37.5, rf + 0.7, 10.0, 3.2, 0.6);
    P.satDish(b, rng, 36.0, rf + 0.6, 20.5, 2.4, 0.45);
    b.add('polymer', cylGeo(0.85, 0.85, 1.5, 12, 1.0), trs(34.5, rf + 0.75, 18.5), tint(0x2c3a44), 'both');
    b.collider(33.65, rf, 17.65, 35.35, rf + 1.5, 19.35, 'wood');
    b.markCover(34.5, 18.5, 0.85, 0.85, rf + 1.5, 'high', rf);
    P.drum(b, rng, 29.0, 21.2, { y: rf });
    P.drum(b, rng, 29.9, 20.6, { y: rf });
    /* A way off this roof that is not the stair you came up.
       Every elevated position on this map was an out-and-back: one route up, the same
       route down, which makes holding a roof a decision with no exit and makes taking
       one a coin flip. A builder's scaffold against the north elevation is a pure
       descent — 7.0 over the parapet onto a deck at 5.4, then 1.8 m at a time to the
       street. None of those rises is inside the 0.5–1.55 m mantle window, so it cannot
       be climbed, and the roof keeps exactly one way in. */
    P.scaffold(b, rng, 37.6, 6.4, 3.0, 2.4, 3, { levelHeight: 1.8, decks: [1, 2, 3] });
    P.crate(b, rng, 37.6, 5.4, 6.4, 1.0, 0.8, 0.9, { rotY: 0.3 });
    P.groundStain(b, rng, 37.6, 8.9, 1.2, {});
    P.sandbags(b, rng, 33.0, 9.2, 1, 0, 3.2, 3, { y: rf });
    P.rubble(b, rng, this.gravel, 32, 16, 6, 40, { y: rf, hex: 0x8a8172, bounds: [28.2, x1 - 0.5, z0 + 0.6, z1 - 0.6] });

    /* --- interior: somebody lived here ---------------------------------------
       The ground floor used to be a market stall and a warehouse shelf rack on a
       white tile floor. There was not one authored moment anywhere on the map where
       a player could read what a person had been doing; this is that moment, and it
       is placed on the through-route so it cannot be missed. */
    P.domestic(b, rng, 31.0, 18.6, -0.25, { y: TUNING.floorY, bedX: -2.6, bedZ: 1.1, stoveX: 2.4, stoveZ: -1.3, shelfX: 2.5, shelfZ: 1.4 });
    P.battenLight(b, 31.0, 3.1, 18.6, 1.4, 0.4, {});
    // Soot up the wall over the stove, and a washing line strung across the room.
    this._box('brick', 33.2, 34.4, 0.7, 2.6, 17.1, 17.2, { color: shade(0x4a423a, 0.42), collide: false, shadow: 'none' });
    P.laundry(b, rng, new THREE.Vector3(28.4, 2.8, 16.4), new THREE.Vector3(34.6, 2.7, 16.4));
    P.groundStain(b, rng, 33.2, 17.4, 0.9, { y: TUNING.floorY, lum: 0.5 });

    P.crate(b, rng, 26.5, 0, 11.0, 1.4, 1.2, 1.2, { rotY: 0.2, cover: 'high' });
    P.crate(b, rng, 26.6, 1.2, 11.2, 1.1, 0.9, 1.0, { rotY: -0.4 });
    P.crate(b, rng, 35.5, 0, 20.0, 1.3, 1.0, 1.2, { rotY: 0.5, cover: 'low' });
    // A second household upstairs, half packed.
    P.domestic(b, rng, 33.0, 12.0, 1.5, { y: f1, bedX: -2.2, bedZ: -1.2, stoveX: 1.8, stoveZ: 1.4, shelfX: 2.2, shelfZ: -1.2 });
    P.drum(b, rng, 37.0, 18.0, {});
    P.crate(b, rng, 29.0, f1, 11.5, 1.3, 1.1, 1.2, { rotY: 0.15, cover: 'high' });
    P.crate(b, rng, 35.5, f1, 20.5, 1.2, 0.95, 1.1, { rotY: -0.25, cover: 'low' });
    P.rubble(b, rng, this.stones, 30, 13, 4, 26, {});

    // Street furniture along the west face.
    P.jersey(b, 20.4, 10.5, Math.PI / 2 + 0.03);
    P.jersey(b, 20.4, 12.8, Math.PI / 2);
    P.jersey(b, 20.4, 15.1, Math.PI / 2 - 0.02);
    P.vanWreck(b, rng, 17.2, 4.0, Math.PI / 2 + 0.02, { hex: 0x7a8078 });
    P.hesco(b, rng, 19.4, 27.5, 0, 1, 4, { h: 1.15 });
  }

  /* ------------------------------------------------------------------ */
  /*  EAST LANE — workshop and the flanking alley                        */
  /* ------------------------------------------------------------------ */

  _workshop() {
    const rng = this.rng;
    const b = this.b;
    const x0 = 29, x1 = 44, z0 = -26, z1 = -8;
    const rf = TUNING.workshopRoof;
    const col = vary(rng, 0x998f7e, 0.05);

    const top = this._shell({
      x0, x1, z0, z1, h: rf, surface: 'concrete', color: col,
      west: [{ a0: -16, a1: -11.5, y0: 0, y1: 4.0 }],
      north: [{ a0: 38, a1: 40.5, y0: 0, y1: 2.4 }],
      parapet: 0.85,
      skip: ['w'], // the external stair lands on this edge
    });

    // External stair on the west face -> roof.
    P.stairs(b, { x: 27.3, y: 0, z: -29.96, dirZ: 1, width: 1.8, steps: 32, rise: top / 32, run: 0.28, surface: 'concrete', color: col });
    this._box('concrete', 26.3, 29.4, top - 0.3, top, -21.2, -19.2, { color: tint(0x968e80) });

    /* --- interior: a workshop, not an empty box ------------------------------
       It was labelled a workshop and had none of the five things that make a room
       one: a bench, a board of tools, a pit, marked floor, and a light you can see
       is on. The ceiling was also a single flat plane 5.6 m up, which is the surface
       the player stares at from the shutter door. */
    P.purlins(b, x0 + 0.7, x1 - 0.7, z0 + 0.7, z1 - 0.7, rf - 0.05, { along: 'x', pitch: 1.9, depth: 0.26 });
    for (let i = 0; i < 4; i++) P.battenLight(b, 36.5, rf - 0.55, z0 + 3.4 + i * 4.2, 2.6, 0, {});
    P.battenLight(b, 41.0, 3.1, -12.0, 1.8, Math.PI / 2, {});
    P.inspectionPit(b, rng, 35.5, -17.6, 1.3, 5.2, 0, { y: TUNING.floorY, depth: 1.3 });
    P.floorMarking(b, rng, 35.5, -17.6, 2.6, 6.4, 0, { y: TUNING.floorY, kind: 'hatch' });
    P.floorMarking(b, rng, 31.0, -17.0, 3.2, 14, 0, { y: TUNING.floorY });
    P.workBench(b, rng, 42.6, -21.5, 5.0, Math.PI / 2, {});
    P.toolBoard(b, rng, x1 - 0.42, 1.75, -21.5, 3.2, 1.1, -Math.PI / 2);
    P.workBench(b, rng, 33.0, z0 + 0.9, 3.0, 0, {});
    P.groundStain(b, rng, 35.4, -13.5, 1.8, { y: TUNING.floorY });
    P.groundStain(b, rng, 39.0, -19.5, 1.2, { y: TUNING.floorY });
    P.truckWreck(b, rng, 35.5, -21.0, 0.06, { hex: 0x4f5f6a });
    P.shelfRack(b, rng, 42.0, -15.0, 8.0, Math.PI / 2, { h: 4.2 });
    P.palletStack(b, rng, 32.0, -11.0, 5, { rotY: 0.2 });
    P.crate(b, rng, 33.6, 0, -12.4, 1.4, 1.2, 1.3, { rotY: -0.3, cover: 'high' });
    for (const [dx, dz] of [[31.4, -14.6], [32.2, -15.4], [31.0, -15.8]]) P.drum(b, rng, dx, dz, {});
    P.gasBottles(b, rng, 38.0, -10.0, 4, { rotY: 0 });
    P.tireStack(b, rng, 40.5, -23.5, 6);
    P.tireStack(b, rng, 41.4, -22.4, 4);
    P.generator(b, rng, 41.5, -10.2, Math.PI / 2);
    P.oilTank(b, rng, 30.9, -23.6, 0, { r: 0.62, len: 2.6 });
    P.cableReel(b, rng, 39.6, -12.6, 0.7, { r: 0.8, w: 0.7 });
    for (const sz of [-13.8]) P.shutter(b, rng, 28.65, 2.1, sz, 4.2, 3.8, Math.PI / 2);
    for (let i = 0; i < 3; i++) P.window_(b, rng, x1, 3.4, z0 + 4 + i * 5, 1.2, 1.4, Math.PI / 2, {});
    P.pipeRun(b, x0 - 0.38, 4.8, -17, 15, 'z', { r: 0.1, color: tint(0x6a6255) });

    // Roof kit and cover.
    P.acUnit(b, rng, 34, top, -20, 0.3);
    P.satDish(b, rng, 41, top + 0.7, -12, 3.0, 0.5);
    P.sandbags(b, rng, 31.5, -16.0, 0, 1, 3.4, 4, { y: top });
    P.crate(b, rng, 38.0, top, -22.0, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'high' });
    P.rubble(b, rng, this.gravel, 36, -17, 5, 34, { y: top, hex: 0x8a8172, bounds: [x0 + 0.4, x1 - 0.4, z0 + 0.6, z1 - 0.6] });

    // --- C2: the block that narrows the north-east into a tight alley --
    const w0 = 16, w1 = 25.5, wz0 = -30, wz1 = -16;
    const wh = TUNING.alleyBlockRoof;
    const wcol = vary(rng, 0xa08874, 0.05);
    // The west wall's upper courses and the roof over them came down together, and
    // the mound below is what came down. A shell breach costs one option and buys the
    // single thing the interior most needed: a reason to be the way it is — plus a
    // drop-through from the roof onto the mound, so the collapse is also a route.
    const wTop = this._shell({
      x0: w0, x1: w1, z0: wz0, z1: wz1, h: wh, surface: 'brick', color: wcol,
      west: [{ a0: -24, a1: -21, y0: 0, y1: 3.0 }, { a0: -27.4, a1: -23.6, y0: 2.3, y1: wh }],
      south: [{ a0: 18, a1: 21, y0: 0, y1: 3.0 }],
      parapet: 0.85,
      skip: ['s'], // mantle chain arrives on the south edge, off the open lot
      roofHole: { x0: w0 - 0.35, x1: 19.4, z0: -27.6, z1: -23.4 },
    });
    // The mound, directly under the breach and fanning east into the room.
    P.rubbleMound(b, rng, this.stones, 17.9, -25.4, 2.5, 2.35, { dir: 0.15, brickHex: 0xa0563c });
    P.rubble(b, rng, this.stones, 19.6, -25.0, 3.0, 26, { y: TUNING.floorY });

    /* --- the alley block interior: bare brick and one crate, previously --- */
    P.purlins(b, w0 + 0.7, w1 - 0.7, wz0 + 0.7, wz1 - 0.7, wh - 0.02, { along: 'z', pitch: 2.2, depth: 0.22 });
    P.battenLight(b, 22.5, wh - 0.6, -20.0, 2.2, 0, {});
    P.floorMarking(b, rng, 22.0, -22.0, 3.0, 9.0, 0, { y: TUNING.floorY });
    P.workBench(b, rng, w1 - 1.1, -27.6, 3.6, Math.PI / 2, {});
    P.toolBoard(b, rng, w1 - 0.4, 1.75, -27.6, 2.2, 1.0, -Math.PI / 2);
    P.shelfRack(b, rng, 24.2, -19.6, 5.0, Math.PI / 2, { h: 3.0 });
    P.brickPallet(b, rng, 22.6, -28.4, 0.3, { rows: 6, hex: 0xa0563c });
    P.cableReel(b, rng, 20.4, -18.6, 0.9, { r: 0.75, w: 0.65 });
    P.skipBin(b, rng, this.stones, 20.6, -28.0, Math.PI / 2 + 0.05, { len: 3.0 });
    P.groundStain(b, rng, 22.0, -21.0, 1.4, { y: TUNING.floorY });
    // Chain off the open lot: pallets 1.09 -> container 2.5 -> crate 3.7 -> roof 4.9
    P.container(b, rng, 21.0, -14.4, { rotY: 0.02, color: vary(rng, 0x7a3a34, 0.1) });
    P.palletStack(b, rng, 21.0, -11.8, 8, { rotY: 0.2 });
    P.crate(b, rng, 21.0, CH, -14.9, 1.3, 1.2, 1.2, { rotY: 0.15, cover: 'high' });
    P.crate(b, rng, 23.6, 0, -12.6, 1.2, 1.0, 1.1, { rotY: -0.2, cover: 'low' });
    P.drum(b, rng, 27.4, -13.0, {});
    P.drum(b, rng, 26.6, -13.7, {});
    P.concretePipes(b, rng, 28.2, -30.6, 0.05, { r: 0.55, len: 2.6, rows: 3, perRow: 3 });
    P.jersey(b, 21.0, -32.4, 0.03);
    P.jersey(b, 23.3, -32.4, -0.02);
    P.acUnit(b, rng, 20, wTop, -22, 0.2);
    P.satDish(b, rng, 22, wTop + 0.7, -27, 2.2, 0.5);
    P.window_(b, rng, w1, 3.0, -19.0, 1.1, 1.4, Math.PI / 2, { shutter: true });
    P.window_(b, rng, w1, 3.0, -26.5, 1.1, 1.4, Math.PI / 2, { shutter: true });
    P.crate(b, rng, 19.0, 0, -20.0, 1.4, 1.2, 1.3, { rotY: 0.3, cover: 'high' });
    P.rubble(b, rng, this.stones, 27.2, -18, 3, 22, {});

    /* --- the gangway ---------------------------------------------------------
       These two roofs are 3.5 m apart and were two separate dead ends: you climbed the
       alley block's mantle chain, stood on it, and climbed back down the same way. A
       plank bridge laid across the alley block's parapet turns them into one position
       with two entrances and two exits, and because it is 1 m wide, railed on one side
       only and overlooked from the workshop parapet, taking it costs something.

       The heights work out on their own: the deck sits on the alley parapet at 5.75,
       which is a 0.85 m mantle up from that roof (inside the window) and a 0.15 m step
       down from the workshop roof at 5.90, whose west parapet is already omitted for
       the external stair. */
    P.gangway(b, rng, 26.9, -19.0, 3.6, 'x', 5.75, { w: 1.0, rail: 1 });
    P.crate(b, rng, 24.6, wTop, -19.0, 1.1, 0.85, 1.0, { rotY: 0.2, cover: 'low' });
    P.sandbags(b, rng, 22.0, -17.4, 1, 0, 3.0, 3, { y: wTop });
    P.crate(b, rng, 31.8, top, -21.0, 1.2, 1.0, 1.1, { rotY: -0.25, cover: 'high' });
  }

  /* ------------------------------------------------------------------ */
  /*  SOUTH-EAST — shop row                                              */
  /* ------------------------------------------------------------------ */

  _shopRow() {
    const rng = this.rng;
    const b = this.b;
    const x0 = 10, x1 = 32, z0 = 33, z1 = 45;
    const col = vary(rng, 0xa08874, 0.05);

    const top = this._shell({
      x0, x1, z0, z1, h: TUNING.shopRoof, surface: 'brick', color: col, thick: 0.7,
      west: [{ a0: 36, a1: 39, y0: 0, y1: 3.0 }],
      north: [{ a0: 14, a1: 17, y0: 0, y1: 3.0 }, { a0: 24, a1: 27, y0: 0, y1: 3.0 }],
      parapet: 0.8,
      skip: ['n'], // mantle chain arrives from the cross street
    });
    this._box('brick', 20.0, 20.6, 0, TUNING.shopRoof, z0 + 0.7, 40.0, { color: col });

    // Shopfronts onto the west lane. The bays differ in width and in what has happened
    // to them, each carries a name, and the awnings face out of the building rather
    // than into it.
    const names = ['TYRES', 'GRAIN', 'AL FAJR'];
    for (let i = 0; i < 3; i++) {
      const sz = z0 + 2.5 + i * 3.6;
      const bw = 2.4 + (i % 2) * 0.5;
      P.shutter(b, rng, x0 - 0.4, 1.75 + (i % 2) * 0.25, sz, bw, 3.0 + (i % 3) * 0.3, Math.PI / 2);
      P.awning(b, rng, x0 - 0.05, 3.7 + (i % 2) * 0.3, sz, bw + 0.5, 1.7 + (i % 2) * 0.3, -Math.PI / 2, {});
      P.sign(b, rng, x0 - 0.32, 4.3, sz, bw + 0.3, 0.5, -Math.PI / 2, [0x7a3a34, 0x2f4f60, 0x8a5a20][i],
        { text: names[i] });
      P.goodsPile(b, rng, x0 - 1.3, 0.02, sz + 1.1, rng() * 3, 0.6);
    }
    P.hangingSign(b, rng, x0, 3.1, z0 + 6.4, -Math.PI / 2, 'CAFE', { w: 1.0, h: 0.46, hex: 0x2f6070 });
    P.sign(b, rng, x0 - 0.55, 4.1, z0 + 10.6, 3.2, 0.8, -Math.PI / 2, 0x2f4f60, { text: 'SHOP ROW' });
    this._glow(x0 - 0.62, 4.1, z0 + 10.6, 0.05, 0.42, 2.7, Math.PI / 2, 0xffc27a, 3.2);
    for (let i = 0; i < 3; i++) P.wallAC(b, rng, x0 - 0.48, 3.5, z0 + 1.4 + i * 4.2, -Math.PI / 2);

    /* --- the inside of the shop row -----------------------------------------
       A 22 x 12 x 4.4 m shell with one partition wall and nothing else in it: the
       largest undressed interior left on the map, and it is on the route between the
       south cross street and the spawn. Two shop units and a back store. */
    P.purlins(b, x0 + 0.9, x1 - 0.9, z0 + 0.9, z1 - 0.9, TUNING.shopRoof - 0.05, { along: 'x', pitch: 2.0, depth: 0.2 });
    for (let i = 0; i < 3; i++) P.battenLight(b, 15.5, 3.3, z0 + 3.0 + i * 4.0, 2.2, 0, {});
    P.battenLight(b, 26.0, 3.3, 38.0, 2.6, Math.PI / 2, {});
    // Counter across the front unit, with the stock behind it.
    this._box('brick', 13.2, 18.6, 0, 1.05, 39.0, 39.6, { color: vary(rng, 0xa8927c, 0.06), cover: 'low' });
    this._box('wood', 13.1, 18.7, 1.05, 1.13, 38.9, 39.7, { color: vary(rng, 0x8a6a44, 0.1), collide: false, shadow: 'cast' });
    for (let i = 0; i < 4; i++) P.goodsPile(b, rng, 14.1 + i * 1.3, 1.13, 39.3, rng() * 3, 0.5);
    P.shelfRack(b, rng, 12.0, 41.0, 6.0, Math.PI / 2, { h: 3.0 });
    P.sackStack(b, rng, 17.2, 41.6, 0.3, { rows: 5, cols: 2, deep: 2 });
    P.sackStack(b, rng, 18.9, 42.2, -0.4, { rows: 3, cols: 1, deep: 2, tarp: false });
    P.timberStack(b, rng, 24.0, 43.2, Math.PI / 2 + 0.04, { len: 3.4, w: 1.0, courses: 6 });
    P.workBench(b, rng, 28.5, 35.0, 3.0, 0, {});
    P.toolBoard(b, rng, 28.5, 1.8, z0 + 0.5, 2.0, 1.0, 0);
    P.crate(b, rng, 22.5, 0, 36.4, 1.3, 1.1, 1.2, { rotY: 0.25, cover: 'high' });
    P.crate(b, rng, 22.6, 1.1, 36.5, 1.0, 0.9, 0.9, { rotY: -0.4 });
    P.tireStack(b, rng, 13.4, 35.2, 5);
    P.tireStack(b, rng, 14.3, 34.4, 3);
    P.floorMarking(b, rng, 25.0, 39.5, 3.0, 8.0, 0, { y: TUNING.floorY });
    P.groundStain(b, rng, 26.5, 37.0, 1.4, { y: TUNING.floorY });
    P.rubble(b, rng, this.stones, 30.0, 42.0, 2.6, 18, { y: TUNING.floorY });

    // Mantle chain onto the shop roof from the cross street.
    P.container(b, rng, 13.0, 31.2, { rotY: 0.02, color: vary(rng, 0x9b8340, 0.1) });
    P.palletStack(b, rng, 9.4, 30.6, 8, { rotY: 0.15 });
    P.crate(b, rng, 12.0, CH, 31.8, 1.4, 1.2, 1.3, { rotY: 0.2, cover: 'high' });
    P.crate(b, rng, 15.4, 0, 30.6, 1.2, 1.0, 1.1, { rotY: -0.3, cover: 'low' });

    P.acUnit(b, rng, 15, top, 38, 0.4);
    P.satDish(b, rng, 24, top + 0.7, 36, 2.8, 0.5);
    P.laundry(b, rng, new THREE.Vector3(11.5, top + 2.0, 36), new THREE.Vector3(11.5, top + 1.9, 43));
    P.sandbags(b, rng, 18.0, 34.2, 1, 0, 3.4, 4, { y: top });
    P.rubble(b, rng, this.gravel, 22, 39, 6, 34, { y: top, hex: 0x8a8172, bounds: [x0 + 0.5, x1 - 0.5, z0 + 0.4, z1 - 0.6] });

    P.marketStall(b, rng, 16.5, 38.0, 0, { w: 2.4 });
    P.crate(b, rng, 25.0, 0, 42.0, 1.4, 1.2, 1.3, { rotY: 0.3, cover: 'high' });
    P.palletStack(b, rng, 28.5, 36.0, 4, { rotY: -0.2 });
    P.drum(b, rng, 22.5, 43.0, {});
  }

  /* ------------------------------------------------------------------ */
  /*  NORTH — colonnade block with a terrace                             */
  /* ------------------------------------------------------------------ */

  _northBlock() {
    const rng = this.rng;
    const b = this.b;
    const x0 = -22, x1 = 12, z0 = -H + 1, z1 = -42, h = 9.5;
    const col = vary(rng, 0x9e968a, 0.04);

    this._box('concrete', x0, x1, 0, h, z0, z1, { color: col });
    this.facades.push({ x0, x1, z0, z1, h, surface: 'concrete' });
    this._box('concrete', x0 - 0.5, x1 + 0.5, h, h + 0.4, z0, z1 + 0.5, { color: tint(0x8f8778) });
    this._parapet(x0 - 0.5, x1 + 0.5, z0, z1 + 0.5, h + 0.4, 0.9, { color: tint(0x9a9285), skip: ['n'] });

    // Colonnade: pillars carrying a 3.5 m canopy that doubles as a firing terrace.
    for (let i = 0; i < 6; i++) {
      const px = x0 + 2 + i * 6.0;
      this._box('concrete', px - 0.46, px + 0.46, 0, 3.2, -40.9, -39.9, { color: col, cover: 'high' });
    }
    this._box('concrete', x0 - 0.4, x1 + 0.4, 3.2, 3.5, -42.0, -39.6, { color: tint(0x968e80) });
    this._box('concrete', x0 - 0.4, x1 + 0.4, 3.5, 4.42, -39.9, -39.6, { color: tint(0x9a9285), cover: 'low' });
    this._box('concrete', x0 - 0.4, x0 - 0.1, 3.5, 4.42, -42.0, -39.9, { color: tint(0x9a9285), cover: 'low' });
    this._box('concrete', x1 + 0.1, x1 + 0.4, 3.5, 4.42, -42.0, -39.9, { color: tint(0x9a9285), cover: 'low' });

    // Blown-out corner: a rubble ramp onto the terrace, and a hole in the facade.
    P.rubbleMound(b, rng, this.stones, -20.6, -38.0, 3.4, 2.85, { dir: Math.PI / 2, brickHex: 0x9a8a72 });
    P.rubbleMound(b, rng, this.stones, -24.6, -38.4, 2.6, 1.5, { dir: 2.4, brickHex: 0x9a8a72 });
    P.rubble(b, rng, this.stones, -18, -38, 6, 60, {});
    this._box('concrete', x0 - 1.2, x0 + 3.5, 4.42, h, z1 - 0.2, z1 + 0.6, { color: col, collide: false, shadow: 'cast' });

    /* A perfect 7 x 2 grid of identical windows over a 34 m facade, and no way in at
       all. Both halves of that are fixed here: the bays are grouped 3 / 2 / 2 with a
       wider joint between the groups, the openings differ in width and head height,
       the upper row is dropped on two of them, and the middle of the facade now has
       an entrance a person could actually use. */
    const cols = [2.4, 6.6, 10.8, 17.6, 21.4, 27.6, 31.6];
    for (let i = 0; i < cols.length; i++) {
      const px = x0 + cols[i];
      const w = 1.15 + (i % 3) * 0.16;
      P.window_(b, rng, px, 5.9 + (i % 2) * 0.16, z1, w, 1.7, 0, { shutter: true });
      if (i !== 1 && i !== 4) P.window_(b, rng, px, 8.2, z1, w, 1.05 + (i % 2) * 0.2, 0, {});
      if (i === 2 || i === 5) this._glow(px, 5.9, z1 + 0.14, 1.0, 1.5, 0.04, 0, 0xffc27a, 3.2);
    }
    // Entrance: a projecting portal with a dark recess behind it, between the third
    // and fourth colonnade pillars, reached off the terrace ramp.
    const ex = x0 + 14.2;
    this._box('concrete', ex - 1.5, ex + 1.5, 0, 3.0, z1 - 0.1, z1 + 0.55, { color: tint(0xa8a08e) });
    this._box('concrete', ex - 1.1, ex + 1.1, 0, 2.6, z1 + 0.1, z1 + 0.62, { color: shade(0x2e2822, 0.2), collide: false, shadow: 'none' });
    this._box('concrete', ex - 1.8, ex + 1.8, 3.0, 3.34, z1 - 0.1, z1 + 0.95, { color: tint(0xb0a894), collide: false, shadow: 'cast' });
    for (const sgn of [-1, 1]) {
      this._box('concrete', ex + sgn * 1.28 - 0.16, ex + sgn * 1.28 + 0.16, 0, 3.0, z1 + 0.3, z1 + 0.62,
        { color: tint(0xb0a894), collide: false, shadow: 'cast' });
      // Double doors, one hanging open.
      P.angled(b, 'wood', ex + sgn * 0.55, 1.15, z1 + 0.55 + (sgn > 0 ? 0.18 : 0), 1.0, 2.3, 0.07,
        0, sgn > 0 ? -0.42 : 0, 0, { color: vary(rng, 0x4a5a52, 0.12, 0.04, { sat: 0.95 }) });
    }
    this._box('concrete', ex - 1.7, ex + 1.7, 0, 0.16, z1 + 0.55, z1 + 1.25, { color: tint(0xb3aa97) });
    P.sign(b, rng, ex, 3.62, z1 + 0.9, 2.6, 0.62, 0, 0x2f4f60, { text: 'ENTRANCE' });
    P.plant(b, rng, ex + 2.1, 0.02, z1 + 1.0, 0.8);
    P.litter(b, rng, ex - 1.9, z1 + 1.1);
    // Both of these were authored 0.45 m *inside* the elevation — a sign and an 8 m
    // pipe run buried in the masonry, which is the same class of mistake as an awning
    // built into its own wall: correct in code, invisible in the frame.
    P.pipeRun(b, x0 + 1.2, 5.5, z1 + 0.45, 8.0, 'x', { r: 0.1, color: tint(0x6a6255) });
    P.sign(b, rng, -5, 7.6, z1 + 0.42, 7.0, 1.4, 0, 0x8a5a20, { text: 'CIVIC STORES' });
    for (const [cx, cz] of [[-14, -44], [-6, -45], [4, -44.5]]) P.satDish(b, rng, cx, h + 1.3, cz, rng() * 6, 0.7);
    P.acUnit(b, rng, -18, h + 0.4, -45, 0.2);
    P.acUnit(b, rng, 8, h + 0.4, -46, -0.3);
    b.add('metalPainted', cylGeo(1.3, 1.3, 2.0, 12, 1.2), trs(0, h + 1.4, -47), tint(0x8d8a7e), 'both');
    b.collider(-1.3, h + 0.4, -48.3, 1.3, h + 2.4, -45.7, 'metal');

    // Terrace furniture — a strong perch, so it needs cover of its own.
    P.sandbags(b, rng, -10, -40.6, 1, 0, 4.0, 3, { y: 3.5 });
    P.sandbags(b, rng, 4, -40.6, 1, 0, 3.2, 3, { y: 3.5 });
    P.crate(b, rng, -16, 3.5, -41.4, 1.3, 1.1, 1.2, { rotY: 0.2, cover: 'high' });
    P.drum(b, rng, 9.4, -41.2, { y: 3.5 });

    // North cross street furniture.
    P.truckWreck(b, rng, 19.5, -35.5, 1.62, { hex: 0x6a5f4a });
    P.container(b, rng, -30.0, -35.4, { rotY: 0.03, color: vary(rng, 0x3f6070, 0.1) });
    P.vanWreck(b, rng, -12.0, -36.5, Math.PI / 2 + 0.02, { hex: 0x7a7f8a });
    P.carWreck(b, rng, 33.0, -34.5, 2.6, { flat: true });
    P.hesco(b, rng, -3.0, -34.0, 1, 0, 5, { h: 1.6 });
    P.jersey(b, 26.0, -37.5, 0.02);
    P.jersey(b, 28.3, -37.5, -0.03);
    P.rubble(b, rng, this.stones, -8, -35, 5, 40, {});
    P.streetLamp(b, -24.0, -36.0, 0, { h: 6.6 });
    P.streetLamp(b, 14.0, -36.5, Math.PI, { h: 6.6 });
  }

  /* ------------------------------------------------------------------ */
  /*  corner filler blocks — the boundary must not read as a corridor    */
  /* ------------------------------------------------------------------ */

  _fillerBlocks() {
    const rng = this.rng;
    const b = this.b;

    const block = (x0, x1, z0, z1, h, surface, hex) => this._block(x0, x1, z0, z1, h, surface, hex);

    // North-east block
    block(17, 45, -H + 1, -40, 7.6, 'brick', 0x9e8672);
    for (let i = 0; i < 5; i++) {
      P.window_(b, rng, 19 + i * 5.4, 4.6, -40.0, 1.3, 1.6, 0, { shutter: true });
      P.window_(b, rng, 19 + i * 5.4, 6.8, -40.0, 1.3, 1.2, 0, {});
    }
    P.satDish(b, rng, 24, 8.6, -44, 2.4, 0.6);
    P.acUnit(b, rng, 33, 7.95, -45, 0.3);
    P.laundry(b, rng, new THREE.Vector3(20, 9.4, -43), new THREE.Vector3(31, 9.2, -43));

    // North-west block and the west boundary fillers
    block(-H + 1, -30, -H + 1, -40, 7.0, 'concrete', 0x9c9282);
    P.satDish(b, rng, -40, 8.0, -45, 1.6, 0.5);
    block(-H + 1, -46.5, -30, -14, 6.0, 'concrete', 0x958d7e);
    block(-H + 1, -46.0, 20, 26, 5.0, 'brick', 0x9e8672);

    // South-east corner
    block(36, 50, 30, 46, 6.2, 'brick', 0x9e8672);
    for (let i = 0; i < 3; i++) P.window_(b, rng, 36.0, 4.2, 33 + i * 4.6, 1.2, 1.5, -Math.PI / 2, { shutter: true });
    P.satDish(b, rng, 42, 7.2, 34, 4.0, 0.55);
    P.acUnit(b, rng, 45, 6.55, 40, 0.2);

    /* --- south-west shop terrace ------------------------------------------
       Four identical bays: same shutter, same awning, same spacing, same height, no
       signage. A terrace is built by different people at different times, and the
       give-away is always the rhythm — so the bays now differ in width, the shutters
       in height and how far they are rolled down, one bay is open with goods spilling
       out of it, one is bricked up, and two carry a sign. */
    block(-30, -14, 36, 48, 4.8, 'brick', 0xa08874);
    const bays = [
      { x: -28.2, w: 2.9, sh: 3.4, awn: 3.2, drop: 0.5, kind: 'shut' },
      { x: -24.6, w: 2.3, sh: 2.7, awn: 2.6, drop: 0.36, kind: 'open' },
      { x: -21.0, w: 3.3, sh: 3.0, awn: 0, drop: 0, kind: 'brickedUp' },
      { x: -17.4, w: 2.6, sh: 3.2, awn: 3.0, drop: 0.46, kind: 'shut' },
    ];
    for (let i = 0; i < bays.length; i++) {
      const bay = bays[i];
      // Pilaster between bays: what actually divides a terrace into shops.
      this._box('brick', bay.x - bay.w / 2 - 0.45, bay.x - bay.w / 2 - 0.15, 0, 4.2, 35.5, 35.9,
        { color: vary(rng, 0xa8927c, 0.06) });
      if (bay.kind === 'brickedUp') {
        this._box('brick', bay.x - bay.w / 2, bay.x + bay.w / 2, 0, bay.sh, 35.52, 35.66,
          { color: vary(rng, 0x9a7060, 0.1, 0.04), collide: false, shadow: 'cast' });
        P.wallPatch(b, rng, bay.x, 2.2, 35.62, 0, {});
      } else if (bay.kind === 'open') {
        // Dark interior, a counter across it, and the stock stacked in the opening.
        this._box('concrete', bay.x - bay.w / 2, bay.x + bay.w / 2, 0, bay.sh, 35.58, 35.7,
          { color: shade(0x2e2822, 0.2), collide: false, shadow: 'none' });
        P.shutter(b, rng, bay.x, bay.sh - 0.35, 35.5, bay.w, 0.7, 0);
        P.crate(b, rng, bay.x - 0.6, 0, 35.0, 0.9, 0.9, 0.7, { rotY: 0.2 });
        P.crate(b, rng, bay.x + 0.6, 0, 34.9, 0.8, 1.2, 0.7, { rotY: -0.3 });
        P.drum(b, rng, bay.x + 1.1, 34.4, {});
      } else {
        P.shutter(b, rng, bay.x, bay.sh / 2 + 0.3, 35.58, bay.w, bay.sh, 0);
      }
      // This elevation looks south, so its outward normal is PI — at 0 the awning was
      // built inside the terrace and the sign's lettering faced the brickwork.
      if (bay.awn > 0) {
        P.awning(b, rng, bay.x, 3.7 + (i % 2) * 0.45, 35.92, bay.awn, 1.7 + (i % 3) * 0.3, Math.PI, { drop: bay.drop });
      }
      if (bay.kind !== 'brickedUp') {
        P.sign(b, rng, bay.x, bay.sh + 0.62, 35.44, bay.w + 0.5, 0.6, Math.PI, [0x2f4f60, 0x8a5a20, 0x7a3a34][i % 3],
          { text: ['MUSTAFA', 'ZAYD', '', 'HARDWARE'][i] });
      }
      if (i === 1) P.hangingSign(b, rng, bay.x + 1.9, 3.2, 35.9, Math.PI, 'TAILOR', { w: 0.95, h: 0.44, hex: 0x486a48 });
      if (bay.kind === 'open') {
        for (let k = 0; k < 3; k++) P.goodsPile(b, rng, bay.x - 0.8 + k * 0.8, 0.02, 34.5 + (k % 2) * 0.5, rng() * 3, 0.55);
      }
      // Doorway to the flat above, between every other pair of shops.
      if (i % 2 === 1) {
        P.opening(b, rng, { axis: 'x', at: 35.7, a0: bay.x + bay.w / 2 + 0.5, a1: bay.x + bay.w / 2 + 1.4, y0: 0, y1: 2.4, thick: 0.5, floor: 0 });
      }
    }
    P.laundry(b, rng, new THREE.Vector3(-28, 6.6, 37), new THREE.Vector3(-17, 6.4, 37));

    // South junk lot between the shop terrace and the gate.
    P.fence(b, -7.0, 49.5, 9, 'x', { h: 2.4 });
    P.fence(b, -11.4, 45.0, 9, 'z', { h: 2.4 });
    P.vanWreck(b, rng, -8.0, 45.5, 0.05, { flat: true, hex: 0x7a7466 });
    P.container(b, rng, -5.0, 50.5, { rotY: Math.PI / 2, color: vary(rng, 0x5a4a55, 0.1) });
    P.carWreck(b, rng, -9.5, 41.0, 1.9, { flat: true });
    P.tireStack(b, rng, -4.5, 42.5, 5);
    for (let i = 0; i < 10; i++) P.junk(b, rng, this.stones, -11 + rng() * 9, 40 + rng() * 10);

    // South-east lot and the cross-street blockers
    P.fence(b, 34.0, 48.5, 10, 'x', { h: 2.4 });
    P.container(b, rng, 30.0, 49.5, { rotY: 0.02, color: vary(rng, 0x3f6070, 0.1) });
    P.timberStack(b, rng, 38.0, 50.0, 0.04, { len: 4.4, w: 1.2, courses: 8 });
    P.sackStack(b, rng, 35.4, 48.6, 0.3, { rows: 5, cols: 2, deep: 2 });
    P.truckWreck(b, rng, 42.5, 24.6, 0.1, { hex: 0x5a5f4a });
    P.busWreck(b, rng, 33.0, 28.0, 0.06);
    P.hesco(b, rng, 47.0, 27.5, 0, 1, 4, { h: 1.6 });
    P.vanWreck(b, rng, -14.5, 27.5, 0.03, { hex: 0x8a8272 });
    P.container(b, rng, -22.0, 27.5, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x9b8340, 0.1) });
    P.carWreck(b, rng, -31.0, 28.5, 0.6, {});
    P.hesco(b, rng, -6.0, 29.5, 1, 0, 5, { h: 1.15 });
    P.sandbags(b, rng, -18.5, 24.6, 1, 0, 3.6, 4, { posts: true });
    P.rubble(b, rng, this.stones, -20, 28, 6, 45, {});
    P.streetLamp(b, -9.9, 30.5, 0, { h: 6.6 });
    P.streetLamp(b, 33.5, -30.0, Math.PI, { h: 6.6 });
  }

  /* ------------------------------------------------------------------ */
  /*  sightline discipline pass                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Deliberate chicanes. The two cross streets and the boundary margins are the only
   * places on the map where geometry alone leaves a straight run, so they get
   * staggered full-width blockers roughly every 15 m. Alternating which half of the
   * road is blocked keeps them traversable while killing the shooting gallery.
   */
  _breakers() {
    const rng = this.rng;
    const b = this.b;

    // --- east boundary margin -------------------------------------------
    // Measured at 74 m from x = 45 looking north: the longest sightline on the map
    // by a wide margin, and the east margin's whole length was one clear lane. The
    // two existing pockets are now closed off from each other by a mass that spans
    // the margin, leaving a 2.8 m dogleg at x ≈ 42 to get through.
    this._block(45.5, 51, -30, -15, 6.6, 'concrete', 0x958d7e);
    this._block(44.0, 51, -8.0, 2.0, 5.2, 'concrete', 0x968e7e);
    this._block(45.5, 51, 4.5, 17, 5.4, 'brick', 0x9e8672);
    P.satDish(b, rng, 47.5, 7.6, -22, 4.6, 0.55);
    P.acUnit(b, rng, 48.5, 5.75, 9, 0.2);
    P.concretePipes(b, rng, 47.4, 20.6, 0.08, { r: 0.55, len: 2.6, rows: 2, perRow: 3 });
    P.oilTank(b, rng, 46.6, -11.4, Math.PI / 2 + 0.05, { r: 0.7, len: 3.0 });
    P.rubbleMound(b, rng, this.stones, 48.0, -34.5, 2.6, 1.6, { dir: -Math.PI / 2 });
    for (let i = 0; i < 6; i++) P.junk(b, rng, this.stones, 46 + rng() * 5, -14 + rng() * 16);

    /* --- the strip along the south wall behind the spawn ---
       Every blocker in this pass used to be a container, and the yard had 56 of them
       against one scaffold and two forklifts. A blocker's *job* is to stop a sightline
       and give something to hide behind; almost anything heavy does that, and what it
       actually is is free storytelling. So the ones that were only ever boxes are now
       a van, a timber stack, a water bowser, a lock-up — chosen for what a place like
       this would have standing there. */
    P.timberStack(b, rng, 44.0, 46.8, 0.03, { len: 4.6, w: 1.25, courses: 9 });
    P.sackStack(b, rng, 41.6, 47.4, -0.2, { rows: 5, cols: 2, deep: 2 });
    P.rubbleMound(b, rng, this.stones, 24.5, 47.4, 2.6, 2.2, { dir: -Math.PI / 2 });
    P.container(b, rng, 13.0, 47.6, { rotY: 0.02, color: vary(rng, 0x46685c, 0.1) });
    P.rubbleMound(b, rng, this.stones, 33.5, 43.0, 2.4, 2.0, { dir: Math.PI });
    P.container(b, rng, -34.0, 47.4, { rotY: 0.04, color: vary(rng, 0x7a3a34, 0.1) });
    P.tireStack(b, rng, 18.5, 48.5, 5);
    for (let i = 0; i < 8; i++) P.junk(b, rng, this.stones, 14 + rng() * 32, 44 + rng() * 6);

    // --- west boundary margin ---
    P.container(b, rng, -48.5, -36.0, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x3f6070, 0.1) });
    P.bowser(b, rng, -48.6, -12.5, Math.PI / 2, { r: 0.66, len: 2.4 });
    P.timberStack(b, rng, -48.8, -10.0, Math.PI / 2 + 0.03, { len: 4.2, w: 1.1, courses: 7 });
    P.kiosk(b, rng, -48.6, 17.0, { w: 2.8, d: 2.4, h: 2.9, rotY: Math.PI / 2, hex: 0x9a9082 });
    P.rubbleMound(b, rng, this.stones, -48.5, 47.0, 3.0, 1.9, { dir: 0 });
    P.container(b, rng, -46.0, 50.0, { rotY: 0.04, color: vary(rng, 0x46685c, 0.1) });
    P.vanWreck(b, rng, -33.0, 50.5, 0.02, { flat: true, hex: 0x8a7f6e });

    // --- north cross street (z −39 … −32) --------------------------------
    // Every blocker used to sit at z ≈ −34, which left the whole z = −39 kerb line
    // swept clean and gave a 60 m run down it. They now alternate between the two
    // kerbs, so the road stays traversable and neither edge of it is a lane.
    P.container(b, rng, -40.0, -34.8, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x6d7a52, 0.1), sleepers: true });
    P.rubbleMound(b, rng, this.stones, -24.5, -34.0, 3.2, 2.4, { dir: Math.PI / 2 });
    P.hesco(b, rng, -24.0, -37.8, 0, 1, 3, { h: 1.6 });
    P.vanWreck(b, rng, -8.5, -34.6, Math.PI / 2 + 0.03, { hex: 0x7d8272 });
    P.hesco(b, rng, -10.5, -37.4, 0, 1, 3, { h: 1.6 });
    P.truckWreck(b, rng, 6.0, -34.9, Math.PI / 2 + 0.04, { hex: 0x59604c });
    P.container(b, rng, 30.0, -34.6, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x8a4a32, 0.1) });
    P.rubbleMound(b, rng, this.stones, 43.5, -33.4, 2.8, 2.1, { dir: Math.PI / 2 });
    P.hesco(b, rng, 41.5, -37.0, 0, 1, 4, { h: 1.15 });
    // The north kerb line itself, staggered against the ones above and deliberately
    // built from four different things rather than four more containers.
    P.oilTank(b, rng, -33.0, -38.2, 0.05, { r: 0.72, len: 3.0 });
    P.kiosk(b, rng, -17.5, -38.0, { w: 3.0, d: 2.2, h: 2.8, rotY: Math.PI, hex: 0x9a9082 });
    P.concretePipes(b, rng, 0.5, -38.2, 0.04, { r: 0.55, len: 2.6, rows: 2, perRow: 3 });
    P.cableReel(b, rng, 15.5, -38.3, 1.4, { r: 1.0, w: 0.9 });
    P.cableReel(b, rng, 17.4, -38.1, 0, { r: 0.85, w: 0.75, lying: true });
    P.skipBin(b, rng, this.stones, 34.5, -38.2, 0.03, { len: 3.6 });
    P.brickPallet(b, rng, 24.0, -38.4, 0.2, { rows: 7 });
    P.brickPallet(b, rng, 25.3, -38.0, -0.3, { rows: 5 });

    // --- south cross street (z +24 … +31) ------------------------------
    P.container(b, rng, -36.0, 27.6, { rotY: Math.PI / 2, color: vary(rng, 0x5a4a55, 0.1) });
    P.rubbleMound(b, rng, this.stones, -27.5, 25.8, 2.6, 2.2, { dir: -Math.PI / 2 });
    P.vanWreck(b, rng, -2.0, 27.4, Math.PI / 2 + 0.02, { hex: 0x8a8579 });
    P.hesco(b, rng, -4.6, 24.4, 1, 0, 3, { h: 1.6 });
    P.container(b, rng, 12.0, 27.6, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x9b8340, 0.1) });
    P.vanWreck(b, rng, 24.5, 27.6, Math.PI / 2 + 0.03, { hex: 0x6f7a72 });
    P.rubbleMound(b, rng, this.stones, 43.0, 27.8, 2.8, 2.3, { dir: Math.PI });
    // The verges north and south of the road get their own line of junk.
    P.rubbleMound(b, rng, this.stones, -44.0, 22.5, 2.4, 2.0, { dir: Math.PI / 2 });
    P.rubbleMound(b, rng, this.stones, -33.0, 23.4, 2.4, 2.1, {});
    P.rubbleMound(b, rng, this.stones, -16.5, 23.4, 2.2, 2.0, {});
    P.bowser(b, rng, 6.2, 23.6, 0.03, { r: 0.68, len: 2.4 });
    P.rubbleMound(b, rng, this.stones, 21.0, 23.6, 2.2, 2.0, {});
    P.timberStack(b, rng, -30.0, 22.4, 0.03, { len: 4.4, w: 1.2, courses: 8 });
    P.rubbleMound(b, rng, this.stones, 4.0, 31.8, 2.4, 2.0, {});
    P.container(b, rng, 30.5, 32.0, { rotY: 0.02, color: vary(rng, 0x3f6070, 0.1) });
    P.rubbleMound(b, rng, this.stones, 44.5, 32.4, 2.4, 2.1, {});

    // --- market street: an extra jog on the east half ------------------
    P.truckWreck(b, rng, 5.6, 29.4, 1.1, { hex: 0x6a5f4a });
    P.vanWreck(b, rng, 6.4, 5.4, 0.05, { hex: 0x7a8a7e });
    P.crate(b, rng, 3.2, 0, 6.2, 1.4, 1.3, 1.3, { rotY: 0.3, cover: 'high' });

    /* --- the open band at z ≈ −9, the widest east-west run on the map ---
       Six containers in a line was the worst instance of the habit: a row of identical
       6 m boxes across the middle of the map, all doing one job. The blockers that
       matter to the sightline are still 2.3 m or taller — a lock-up, a van, a stack of
       pipes — but no two of them are now the same object. */
    P.container(b, rng, -31.5, -8.8, { rotY: 0.03, color: vary(rng, 0x3f6070, 0.1) });
    P.kiosk(b, rng, -18.5, -8.6, { w: 3.2, d: 2.6, h: 2.9, rotY: 0.02, hex: 0x9c9382 });
    P.rubbleMound(b, rng, this.stones, -25.5, -9.4, 2.4, 2.1, { dir: 0 });
    P.container(b, rng, -9.0, -9.2, { rotY: 0.04, color: vary(rng, 0x8a4a32, 0.1) });
    P.vanWreck(b, rng, 3.0, -9.6, 0.02, { hex: 0x8a8272 });
    P.concretePipes(b, rng, 13.5, -8.8, 0.03, { r: 0.58, len: 2.8, rows: 3, perRow: 3 });
    P.timberStack(b, rng, 11.0, -9.6, 0.05, { len: 4.0, w: 1.1, courses: 6 });
    P.container(b, rng, 26.5, -9.4, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x46685c, 0.1) });
    P.truckWreck(b, rng, 37.0, -6.2, 0.08, { hex: 0x5a5f4a });
    P.rubble(b, rng, this.stones, 30, -3, 6, 44, {});

    // --- north leg and the open lot east of the plaza -------------------
    P.vanWreck(b, rng, 20.0, -5.4, 0.03, { hex: 0x8a7f6e });
    P.hesco(b, rng, 26.0, -3.0, 1, 0, 5, { h: 1.95 });
    P.carWreck(b, rng, 33.5, -2.0, 1.2, {});
    P.container(b, rng, 40.0, 0.5, { rotY: Math.PI / 2 + 0.02, color: vary(rng, 0x3f6070, 0.1) });
    // A lock-up, not a stack: this spot has to stop the 61 m east-west line across the
    // open lot, and only something taller than an eye at 1.67 can do that. The timber
    // beside it is the low half of the same pile.
    P.kiosk(b, rng, 24.5, 0.2, { w: 3.2, d: 2.6, h: 2.9, rotY: 0.03, hex: 0x9a9082 });
    P.bowser(b, rng, 22.4, 1.9, 0.5, { r: 0.68, len: 2.4 });
    P.timberStack(b, rng, 22.6, -1.6, 0.4, { len: 3.6, w: 1.0, courses: 6 });
    P.hesco(b, rng, -1.5, -20.0, 0, 1, 4, { h: 1.6 });
    P.concretePipes(b, rng, 16.9, -21.0, Math.PI / 2 + 0.05, { r: 0.46, len: 2.2, rows: 2, perRow: 2 });

    // --- east lane: chicanes hugging the west kerb ---------------------
    P.rubbleMound(b, rng, this.stones, 16.4, 10.0, 2.0, 1.9, { dir: Math.PI });
    P.carWreck(b, rng, 17.4, -3.5, 0.15, { flat: true });
    P.rubbleMound(b, rng, this.stones, 22.5, -8.0, 2.0, 1.9, { dir: -Math.PI / 2 });

    // --- west alley ----------------------------------------------------
    P.container(b, rng, -21.5, 1.4, { rotY: 0.02, color: vary(rng, 0x9b8340, 0.1), sleepers: true });
    P.timberStack(b, rng, -21.6, 18.0, Math.PI / 2 + 0.03, { len: 4.6, w: 1.2, courses: 9 });
    P.sackStack(b, rng, -20.4, 15.6, 0.2, { rows: 5, cols: 2, deep: 2 });
    P.rubbleMound(b, rng, this.stones, -25.5, 12.0, 2.2, 2.0, { dir: 0 });
  }

  /* ------------------------------------------------------------------ */
  /*  overhead layer — poles, cables, banners                            */
  /* ------------------------------------------------------------------ */

  _overhead() {
    const rng = this.rng;
    const b = this.b;
    // A pole line marching north along the market street: the leading lines that
    // carry the eye from the spawn to the monument and the crane behind it.
    const poles = [
      [9.9, 46.5, 8.2], [9.9, 31.5, 8.4], [9.8, 14.5, 8.2], [10.4, -2.5, 8.6], [11.4, -20.0, 8.2],
      [-9.9, 40.0, 8.0], [-10.0, 26.5, 8.2], [-9.9, 4.0, 8.0],
    ];
    for (const [px, pz, ph] of poles) P.utilityPole(b, rng, px, pz, { h: ph, lean: (rng() - 0.5) * 0.05 });
    for (let i = 0; i < poles.length - 1; i++) {
      const a = poles[i];
      const c = poles[i + 1];
      if (Math.sign(a[0]) !== Math.sign(c[0])) continue;
      for (const off of [-0.9, 0, 0.9]) {
        P.cable(b, new THREE.Vector3(a[0], a[2] - 0.6, a[1] + off), new THREE.Vector3(c[0], c[2] - 0.6, c[1] + off), 0.9);
      }
      P.cable(b, new THREE.Vector3(a[0], a[2] - 1.5, a[1]), new THREE.Vector3(c[0], c[2] - 1.5, c[1]), 1.2);
    }
    P.cable(b, new THREE.Vector3(9.8, 6.8, 14.5), new THREE.Vector3(15.6, 5.0, 15.0), 0.6);
    P.cable(b, new THREE.Vector3(-9.9, 6.6, 4.0), new THREE.Vector3(-25.8, 5.6, 6.5), 1.4);
    P.cable(b, new THREE.Vector3(10.4, 7.0, -2.5), new THREE.Vector3(-3.8, 6.2, -14.4), 1.5);
    P.cable(b, new THREE.Vector3(11.4, 6.6, -20.0), new THREE.Vector3(28.8, 5.8, -22.0), 1.6);
    P.cable(b, new THREE.Vector3(9.9, 6.6, 46.5), new THREE.Vector3(-9.9, 6.4, 40.0), 1.8);
    P.cable(b, new THREE.Vector3(-10.0, 6.8, 26.5), new THREE.Vector3(-25.9, 5.4, 25.0), 1.5);
    P.cable(b, new THREE.Vector3(9.9, 6.9, 31.5), new THREE.Vector3(10.3, 4.6, 33.2), 0.2);

    /* Banners strung over the market. These hang between the player and a low sun for
       the whole length of the street, which is the single most backlit position any
       object on this map occupies — and they were opaque 2 cm boxes, so they read as a
       row of black holes punched in the sky. Same sheet, same cloth material, and they
       curl the way a hanging pennant does in still air. */
    for (let i = 0; i < 4; i++) {
      const z = 10.5 + i * 3.8;
      P.cable(b, new THREE.Vector3(-10.5, 5.2, z), new THREE.Vector3(10.4, 5.2, z), 0.4);
      for (let k = 0; k < 5; k++) {
        const bw = 0.5 + rng() * 0.18;
        const bh = 0.62 + rng() * 0.24;
        const curl = 0.05 + rng() * 0.08;
        const px = -7 + k * 3.5 + (rng() - 0.5) * 0.5;
        b.add('fabric',
          sheetGeo(bw, bh, 4, 4,
            (u, v) => Math.cos(u * Math.PI) * curl * (0.35 + v) + v * v * curl * 0.5,
            {
              color: vary(rng, [0xb0563c, 0x2f6070, 0x9c8a4a, 0x486a48][k % 4], 0.14, 0.05, { sat: 0.92 }),
              shade: (u, v) => 0.76 + 0.32 * (1 - Math.abs(u) * 1.7) - v * 0.08,
            }),
          trs(px, 4.5 - Math.sin(((px + 10.5) / 20.9) * Math.PI) * 0.4, z, Math.PI / 2, (rng() - 0.5) * 0.3, 0),
          null, 'both', true, P.CLOTH_MAT);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  facade and street micro-detail                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Walks every registered building face and hangs the tertiary layer on it —
   * downpipes and their stains, meter boxes, failed render, wall-base spoil, weeds in
   * the joint. This is the layer most levels stop short of: primary masses and props
   * read as a set, and what makes it read as a *place* is the hundred small things
   * nobody would ever model by hand.
   */
  _facadeDetail() {
    const rng = this.rng;
    const b = this.b;

    for (const f of this.facades) {
      const faces = [
        { axis: 'x', at: f.z0, rotY: Math.PI, a0: f.x0, a1: f.x1 },  // faces -Z
        { axis: 'x', at: f.z1, rotY: 0, a0: f.x0, a1: f.x1 },        // faces +Z
        { axis: 'z', at: f.x0, rotY: -Math.PI / 2, a0: f.z0, a1: f.z1 },
        { axis: 'z', at: f.x1, rotY: Math.PI / 2, a0: f.z0, a1: f.z1 },
      ];
      for (const face of faces) {
        const nx = Math.sin(face.rotY);
        const nz = Math.cos(face.rotY);
        const len = face.a1 - face.a0;
        if (len < 3) continue;
        const put = (t, out) =>
          face.axis === 'x'
            ? [face.a0 + t, face.at + nz * out]
            : [face.at + nx * out, face.a0 + t];

        // Downpipes near the corners and at the odd party wall.
        for (const t of [1.0 + rng() * 0.8, len - 1.0 - rng() * 0.8]) {
          if (rng() < 0.28) continue;
          const [px, pz] = put(t, 0.13);
          // No clearance test here: a downpipe is meant to be against the wall, and
          // testing for solids at 0.13 m out just finds the wall itself every time.
          P.drainpipe(b, rng, px, pz, 0.1, f.h + 0.2, face.rotY, {});
        }
        // Base plinth: the junction between wall and dirt, which every one of these
        // faces was missing. Run once per elevation rather than per station.
        const masonry = f.surface === 'brick' || f.surface === 'concrete';
        if (len > 5 && rng() < 0.85) {
          P.basePlinth(b, rng, { axis: face.axis, at: face.at, from: face.a0, to: face.a1, rotY: face.rotY }, {
            h: 0.34 + rng() * 0.2, out: 0.1, hex: masonry ? 0x8d8578 : 0x8a8276,
          });
        }

        // Meter boxes, wall failures, weeds and spoil along the base.
        const stations = Math.max(2, Math.floor(len / 2.1));
        for (let i = 0; i < stations; i++) {
          const t = (len * (i + 0.5)) / stations + (rng() - 0.5) * 1.2;
          if (t < 0.6 || t > len - 0.6) continue;
          const [px, pz] = put(t, 0.1);
          if (rng() < 0.22) P.conduitBox(b, rng, px, 1.35 + rng() * 0.5, pz, face.rotY);
          // A wall fails according to what it is made of. The old code pasted the
          // *other* material onto every face, which put red brick rectangles on the
          // tin warehouse and grey stickers on the brickwork — either way it read as
          // a texture error, and it was the only saturated colour on the whole map.
          if (masonry) {
            if (rng() < 0.42) P.wallPatch(b, rng, px, 0.9 + rng() * (f.h - 1.8), pz, face.rotY, {});
          } else if (rng() < 0.5) {
            P.panelStain(b, rng, px, 0.9 + rng() * (f.h - 1.6), pz, face.rotY, { drop: 1.8 });
          }
          const [ox, oz] = put(t, 0.85);
          if (b.blocked(ox, oz, 0.4, 0.3)) continue;
          if (rng() < 0.55) P.rubble(b, rng, this.stones, ox, oz, 0.7, 4 + Math.floor(rng() * 5), { scale: 0.7 });
          if (rng() < 0.5) P.plant(b, rng, ox, 0.02, oz, 0.5 + rng() * 0.7);
          if (rng() < 0.35) P.litter(b, rng, ox, oz);
        }

        // --- roof edge: aerials, vents, and the streaks under the coping ----
        const roofStations = Math.max(1, Math.floor(len / 5.5));
        for (let i = 0; i < roofStations; i++) {
          const t = (len * (i + 0.5)) / roofStations + (rng() - 0.5) * 1.6;
          if (t < 0.8 || t > len - 0.8) continue;
          const [rx, rz] = put(t, -0.9);
          const roll = rng();
          if (roll < 0.3) {
            // TV aerial: a mast with a few elements, pure silhouette value.
            P.tube(b, 'metalPainted', rx, f.h + 1.0, rz, 0.022, 2.0, 0, 0, 0, { color: tint(0x6a6255), seg: 5, shadow: 'none' });
            for (let k = 0; k < 4; k++) {
              this._box('metalPainted', rx - 0.34, rx + 0.34, f.h + 1.5 + k * 0.16, f.h + 1.53 + k * 0.16, rz - 0.02, rz + 0.02, {
                tile: 0.2, color: tint(0x6a6255), collide: false, shadow: 'none',
              });
            }
          } else if (roll < 0.5) {
            P.tube(b, 'metalPainted', rx, f.h + 0.45, rz, 0.075, 0.8, 0, 0, 0, { color: tint(0x7a7266), seg: 7, shadow: 'cast' });
            b.add('metalPainted', cylGeo(0.13, 0.13, 0.06, 8, 0.3), trs(rx, f.h + 0.88, rz), tint(0x6a6255), 'none');
          } else if (roll < 0.62) {
            P.satDish(b, rng, rx, f.h + 0.75, rz, rng() * 6, 0.34 + rng() * 0.18);
          }
          // Water streaks below the coping.
          if (rng() < 0.5) {
            const [wx, wz] = put(t + (rng() - 0.5) * 1.4, 0.02);
            this.b.solid('concrete', wx, f.h - 0.7 - rng() * 0.6, wz, 0.28 + rng() * 0.3, 1.2 + rng() * 0.9, 0.02, {
              rotY: face.rotY, color: tint(0x6f6a5e, { lum: 0.72 }), collide: false, shadow: 'none',
            });
          }
        }
      }
    }

    // --- street furniture -------------------------------------------------
    for (const [x, z0, z1] of [[9.4, 6, 24], [-9.4, 6, 24], [9.4, 32, 50], [-9.4, 32, 50]]) {
      for (let z = z0; z < z1; z += 4.4) {
        if (rng() < 0.4) continue;
        if (b.blocked(x, z, 0.5, 0.6)) continue;
        P.bollard(b, rng, x, z);
      }
    }
    P.roadMark(b, rng, 0, 38, 22, 'z');
    P.roadMark(b, rng, 0, 14, 16, 'z');
    P.roadMark(b, rng, 6, -22, 26, 'z');
    P.roadMark(b, rng, -30, 27.5, 40, 'x');
    P.roadMark(b, rng, 20, -35.5, 44, 'x');
    for (const [mx, mz] of [[3.2, 30], [-4.1, 18], [6.4, -4], [-12, 2], [19.4, 8], [-21, 12], [1.5, -26], [24, -34], [-33, 27]]) {
      if (!b.blocked(mx, mz, 0.4, 0.5)) P.manhole(b, rng, mx, mz);
    }

    // --- wind-blown litter, heaviest where walls trap it -------------------
    let n = 0;
    for (let i = 0; i < 4000 && n < 330; i++) {
      const x = (rng() - 0.5) * 2 * (H - 2);
      const z = (rng() - 0.5) * 2 * (H - 2);
      if (b.blocked(x, z, 0.35, 0.5)) continue;
      if (!b.blocked(x, z, 0.35, 2.2) && rng() < 0.75) continue;
      P.litter(b, rng, x, z);
      n++;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  tertiary dressing pass                                             */
  /* ------------------------------------------------------------------ */

  _dressing() {
    const rng = this.rng;
    const b = this.b;

    // Debris hugging the base of whatever it fell off: accepted only where the point
    // is clear to walk but something solid is within about a metre.
    let drift = 0;
    for (let i = 0; i < 4000 && drift < 260; i++) {
      const x = (rng() - 0.5) * 2 * (H - 1);
      const z = (rng() - 0.5) * 2 * (H - 1);
      if (b.blocked(x, z, 0.4, 0.55)) continue;
      if (!b.blocked(x, z, 0.4, 1.5)) continue;
      P.rubble(b, rng, this.gravel, x, z, 0.7 + rng() * 0.9, 4 + Math.floor(rng() * 7), { hex: 0x7f7361, scale: 0.85 });
      drift++;
    }
    // A thin sprinkle away from the walls so the open ground is not surgically clean.
    for (let i = 0; i < 900; i++) {
      const x = (rng() - 0.5) * 2 * (H - 4);
      const z = (rng() - 0.5) * 2 * (H - 4);
      if (b.blocked(x, z, 0.4, 0.7)) continue;
      if (rng() > 0.22) continue;
      P.rubble(b, rng, this.gravel, x, z, 0.5 + rng() * 0.7, 2 + Math.floor(rng() * 3), { hex: 0x776c5b, scale: 0.6 });
    }

    let placed = 0;
    for (let i = 0; i < 520 && placed < 140; i++) {
      const x = (rng() - 0.5) * 2 * (H - 4);
      const z = (rng() - 0.5) * 2 * (H - 4);
      if (b.blocked(x, z, 0.6, 1.2)) continue;
      P.junk(b, rng, this.stones, x, z);
      placed++;
    }
    let plants = 0;
    for (let i = 0; i < 800 && plants < 180; i++) {
      const x = (rng() - 0.5) * 2 * (H - 2);
      const z = (rng() - 0.5) * 2 * (H - 2);
      if (b.blocked(x, z, 0.4, 0.7)) continue;
      const edge = Math.max(Math.abs(x), Math.abs(z)) / H;
      if (rng() > 0.16 + edge * 0.75) continue;
      P.plant(b, rng, x, 0.02, z, 0.6 + rng() * 0.8);
      plants++;
    }
    for (let i = 0; i < 260; i++) {
      const x = (rng() - 0.5) * 2 * (H - 4);
      const z = (rng() - 0.5) * 2 * (H - 4);
      if (b.blocked(x, z, 0.4, 0.6)) continue;
      if (!b.blocked(x, z, 0.4, 1.7)) continue;
      P.rubble(b, rng, this.stones, x, z, 1.0, 3 + Math.floor(rng() * 5), { scale: 0.75, hex: 0x7e766a });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  background silhouette (outside the walls, never collides)          */
  /* ------------------------------------------------------------------ */

  _skyline() {
    const rng = this.rng;
    const b = this.b;

    for (let i = 0; i < 28; i++) {
      const a = rng() * Math.PI * 2;
      const r = 76 + rng() * 84;
      const w = 10 + rng() * 26;
      const d = 10 + rng() * 26;
      const h = 8 + rng() * 26;
      b.add('concrete', boxGeo(w, h, d, 6.0), trs(Math.cos(a) * r, h / 2, Math.sin(a) * r), vary(rng, 0x74705f, 0.07), 'none');
    }

    // Construction crane — the one thing tall enough to anchor the sky.
    const cx = -16, cz = -78;
    b.add('metalPainted', boxGeo(1.6, 42, 1.6, 3.0), trs(cx, 21, cz), tint(0xb0741e), 'none');
    for (let i = 0; i < 10; i++) {
      b.add('metalPainted', boxGeo(2.0, 0.22, 2.0, 2.0), trs(cx, 4 + i * 4.2, cz), tint(0x8f5e18), 'none');
    }
    b.add('metalPainted', boxGeo(52, 1.5, 1.5, 4.0), trs(cx + 14, 43.4, cz), tint(0xb0741e), 'none');
    b.add('metalPainted', boxGeo(4.0, 3.2, 3.0, 2.0), trs(cx - 6, 44.6, cz), tint(0x8f5e18), 'none');
    b.add('metalPainted', boxGeo(2.4, 8, 2.4, 3.0), trs(cx - 11, 47, cz), tint(0xb0741e), 'none');
    P.cable(b, new THREE.Vector3(cx - 11, 51, cz), new THREE.Vector3(cx + 39, 44, cz), 1.2, { r: 0.09 });
    P.cable(b, new THREE.Vector3(cx - 11, 51, cz), new THREE.Vector3(cx - 12, 44, cz), 0.1, { r: 0.09 });
    b.add('metalPainted', boxGeo(0.35, 12, 0.35, 1.0), trs(cx + 24, 37, cz), tint(0x4a4640), 'none');
    b.add('metalPainted', boxGeo(1.6, 1.2, 1.6, 1.0), trs(cx + 24, 30.6, cz), tint(0x3a3630), 'none');

    // Water tower east, minaret north-west: scale references from anywhere on the map.
    const wx = 64, wz = -64;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4;
      b.add('metalPainted', boxGeo(0.6, 20, 0.6, 3.0), trs(wx + Math.cos(a) * 3.4, 10, wz + Math.sin(a) * 3.4), tint(0x6b6558), 'none');
    }
    b.add('metalPainted', cylGeo(4.6, 3.4, 6.0, 14, 3.0), trs(wx, 22.5, wz), tint(0x7d7566), 'none');
    b.add('metalPainted', cylGeo(2.2, 4.6, 2.2, 14, 3.0), trs(wx, 18.5, wz), tint(0x6f6858), 'none');
    b.add('metalPainted', cylGeo(0.1, 3.6, 2.2, 12, 3.0), trs(wx, 26.6, wz), tint(0x8a8272), 'none');

    const mx = -64, mz = 26;
    b.add('concrete', cylGeo(1.9, 2.3, 26, 12, 4.0), trs(mx, 13, mz), tint(0xa39a86), 'none');
    b.add('concrete', cylGeo(2.9, 2.9, 1.4, 12, 3.0), trs(mx, 24.2, mz), tint(0xa8a08c), 'none');
    b.add('concrete', cylGeo(1.5, 1.9, 4.0, 12, 3.0), trs(mx, 27.0, mz), tint(0xa39a86), 'none');
    b.add('concrete', cylGeo(0.05, 1.6, 3.4, 12, 3.0), trs(mx, 30.7, mz), tint(0xa8a08c), 'none');
    b.add('concrete', boxGeo(16, 9, 14, 6.0), trs(mx - 4, 4.5, mz + 13), tint(0x9c937f), 'none');
    b.add('concrete', cylGeo(0.1, 6.5, 4.0, 14, 4.0), trs(mx - 4, 11.2, mz + 13), tint(0x9c937f), 'none');
  }

  /* ------------------------------------------------------------------ */
  /*  AI data                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Cover points are derived from the geometry that was actually placed rather than
   * hand-listed, so they cannot drift out of sync with the art. `normal` points away
   * from the cover: an agent standing at `position` has the obstacle behind it when
   * it faces along `normal`.
   */
  _makeCoverPoints() {
    const b = this.b;
    const phys = this.ctx.physics;
    const out = [];
    const seen = new Set();
    const off = TUNING.coverStandoff;

    for (const c of b.coverBoxes) {
      const baseY = c.y ?? 0;
      const relief = c.top - baseY;
      if (relief < 0.55) continue;
      const height = c.height === 'high' || relief > 1.28 ? 'high' : 'low';
      const faces = [
        [0, -1, c.x, c.z - c.hd - off],
        [0, 1, c.x, c.z + c.hd + off],
        [-1, 0, c.x - c.hw - off, c.z],
        [1, 0, c.x + c.hw + off, c.z],
      ];
      for (const [nx, nz, px, pz] of faces) {
        if (Math.abs(px) > H - 1.5 || Math.abs(pz) > H - 1.5) continue;
        if (b.blocked(px, pz, baseY + 0.9, 0.42)) continue;
        // The standoff point has to land on the same floor the cover stands on —
        // otherwise a rooftop parapet emits points hanging in mid-air off the eave.
        const floor = phys.groundHeight(px, pz, baseY + 1.0, 2.2);
        if (Math.abs(floor - baseY) > 0.5) continue;
        if (floor > 0.15 && !onReachablePlatform(px, pz, floor)) continue;
        const key = `${Math.round(px / 1.5)}:${Math.round(pz / 1.5)}:${nx}:${nz}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          // `normal` faces out of cover, so an agent looking along it has the
          // obstacle at its back and only has to lean to shoot.
          position: new THREE.Vector3(px, floor, pz),
          normal: new THREE.Vector3(nx, 0, nz),
          height,
        });
      }
    }

    // Spread the selection across the map instead of letting one dense yard win.
    const buckets = new Map();
    for (const p of out) {
      const key = `${Math.floor((p.position.x + H) / 13)}:${Math.floor((p.position.z + H) / 13)}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(p);
    }
    const picked = [];
    for (let round = 0; round < 60 && picked.length < TUNING.maxCoverPoints; round++) {
      let added = 0;
      for (const list of buckets.values()) {
        if (round >= list.length) continue;
        picked.push(list[round]);
        added++;
        if (picked.length >= TUNING.maxCoverPoints) break;
      }
      if (!added) break;
    }
    this.coverPoints = picked.length >= 40 ? picked : out.slice(0, Math.max(40, picked.length));
  }

  /** Defensive positions spread over the three lanes and the elevated perches. */
  _makeEnemySpawns() {
    const mz = TUNING.warehouseMezz;
    const wr = TUNING.warehouseRoof;
    const tf = TUNING.tenementFloor;
    const tr = TUNING.tenementRoof;
    const wanted = [
      [-35, 0.1, 12],       // warehouse floor, south bay
      [-42, mz + 0.1, 14],  // warehouse mezzanine
      [-34, wr + 0.1, 8],   // warehouse roof
      [-38, 0.1, -14],      // container yard
      [-30, 0.1, -26],      // container yard, north
      [-42, 1.2, 26.5],     // depot loading dock
      [-13.5, 0.1, 15],     // west market compound
      [-14, 0.1, -20],      // C1 interior
      [-13, 6.0, -22],      // C1 roof
      [0, 0.1, -28],        // north leg
      [7.5, 0.1, -9],       // plaza north approach
      [-12.5, 4.1, 1.5],    // scaffold deck
      [8, 0.1, 2],          // plaza east
      [-4, 3.6, -40.6],     // north colonnade terrace
      [21.5, 0.1, -34],     // north cross street
      [33, 6.0, -18],       // workshop roof
      [35, 0.1, -18],       // workshop interior
      [20, 5.0, -24],       // alley block roof
      [30, 0.1, 15],        // tenement ground floor
      [30, tf + 0.1, 18],   // tenement first floor
      [33, tr + 0.1, 14],   // tenement roof
      [22, 0.1, 20],        // under the tenement balcony
      [13, 4.9, 20],        // east market block roof
      [18, 4.8, 38],        // shop-row roof
      [16, 0.1, 28.5],      // south cross street
      [-19, 0.1, 28.5],     // south cross street, west
      [2, 0.1, 20],         // market
    ];
    const spawns = [];
    for (const [x, y, z] of wanted) {
      if (this.b.blocked(x, z, y + 0.9, 0.4)) continue;
      spawns.push(new THREE.Vector3(x, y, z));
    }
    this.enemySpawns = spawns;
  }

  dispose() {
    this.root.traverse((o) => {
      if (o.isMesh) o.geometry?.dispose?.();
    });
    this.ctx.scene.remove(this.root);
  }
}
